import {
  default as superAgentRequest,
  ProgressEvent,
  SuperAgentStatic,
  SuperAgentRequest,
  Response,
} from 'superagent';

import {getGlobalRequestHandlers} from './utilities/globalRequestHandlers';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type ConfigureAgentHandler = (
  agent: SuperAgentStatic
) => SuperAgentStatic;

export type ConfigureRequestHandler = (
  agent: SuperAgentRequest
) => SuperAgentRequest;

export type CallbackHandler = (err: any, res?: Response) => void;

export type AccountingExportReferenceInResponse = {
  exportNumber: number;
  id: string;
};

export type AccountingReceiptReferenceInResponse = {
  export?: AccountingExportReferenceInResponse;
  id: string;
  receiptNumber: number;
};

export type ActivityFeeInResponse = {
  activityId: string;
  duration: number;
  hourlyRate: Money;
};

export type ActivityInvoiceContainerItemInResponse = {
  duration: number;
  hourlyRate: Money;
};

export type AddFeeCompletersParams = {
  feeCompleterUserIds: Array<string>;
};

export type AddFeesAsItemsParams = {
  feeIds: Array<string>;
};

export type AddQuickFeeParams = {
  amount: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  feeProviderUserId?: string;
  incurredAt?: string;
  multiplier?: string;
  text: string;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type AddQuickFeeResponse = {};

export type AdvancePaymentContainerItemInParams = {
  amount: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
};

export type AdvancePaymentInvoiceContainerItemInResponse = {
  amountBeforeTax: Money;
};

export type AdvancePaymentRequestContainerCreateParams = {
  amountBeforeTax: Money;
  bankAccountId?: string;
  casusId: string;
  description?: string;
  internalNote?: string;
  invoiceDate: string;
  invoiceDueDate: string;
  summary: string;
  templateFileId: string;
};

export type AssignTransactionParams = {
  assignmentParams: AssignmentParams;
  casusDebtorId: string;
};

export type AssignTransactionResponse = {
  createdPaymentId: string;
};

export type AssignedInvoice = {
  id: string;
  invoiceNumber: string;
  invoiceRecipient: string;
  requiresManualAction: boolean;
};

export type AssignmentParams = {
  invoices: Array<InvoiceAssignment>;
  prepaymentParams?: PrepaymentParams;
  remainingAmount: Money;
  repaymentParams?: RepaymentParams;
  roundDifferenceParams?: RoundingDifferenceParams;
  transactionAmount: Money;
  writeOffParams?: WriteOffParams;
};

export type AssignmentPayment = {
  createdAt: string;
  debtorDisplayName: string;
  id: string;
  nonMatchingVatNumber: boolean;
  paymentNumber: string;
  prepaymentType?: 'PREPAYMENT' | 'RETAINER';
  remainingAmount: Money;
  summary: string;
  totalAmount: Money;
  valueDate: string;
  vatNumber: TranslatedVatNumber;
};

export type BillableCasusInResponse = {
  casusNumber: string;
  clientId: string;
  displayNameWithClient: string;
  id: string;
  responsiblePerson: UserInResponse;
  totalBillableOpenFeesBeforeTax: Money;
  totalOpenFeesBeforeTax: Money;
};

export type Booking = {
  accountingReceiptReference?: AccountingReceiptReferenceInResponse;
  amount: Money;
  bookingNumber: string;
  createdAt: string;
  createdBy: UserInResponse;
  hideAmount: boolean;
  id: string;
  invoiceId?: string;
  invoiceNumber?: string;
  paymentBookingDeletionActionAvailable: boolean;
  paymentId?: string;
  paymentNumber?: string;
  settlementRevocationActionAvailable: boolean;
  summary: string;
  transferTargetPaymentId?: string;
  type:
    | 'SETTLEMENT'
    | 'PREPAYMENT_SETTLEMENT'
    | 'WRITE_OFF'
    | 'PAYMENT_REVOCATION'
    | 'ROUNDING_DIFFERENCE_UNDERPAYMENT'
    | 'ROUNDING_DIFFERENCE_OVERPAYMENT'
    | 'REPAYMENT'
    | 'SETTLEMENT_CANCELLATION'
    | 'ROUNDING_DIFFERENCE_CANCELLATION'
    | 'TRANSFER';
  valueDate: string;
  writeOffDeletionActionAvailable: boolean;
};

export type BulkStateUpdateParams = {
  invoiceContainerIds: Array<string>;
  state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
};

export type Camt054WithUnknownIbanCondition = {
  conditionType: 'Camt054WithUnknownIbanCondition';
};

export type Camt054WithoutIbanCondition = {
  conditionType: 'Camt054WithoutIbanCondition';
};

export type CancelInvoiceContainerParams = {
  cancellationDate: string;
  createDuplicateContainer: boolean;
};

export type CancelInvoiceContainerResponse = {
  createdCancellationContainerId: string;
  createdDuplicateContainerId?: string;
};

export type Cancellation = {
  accountingReceiptReference?: AccountingReceiptReferenceInResponse;
  cancellationDate: string;
  cancellationDocument?: InvoiceDocumentInResponse;
  cancellationNumber: string;
  containerId: string;
  createdAt: string;
  createdBy: UserInResponse;
};

export type Casus = {
  casusNumber: string;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  groupName?: string;
  id: string;
  locked: boolean;
  name: string;
  responsibleUser: UserInResponse;
};

export type CasusDebtorContactIds = {
  addressContactId: string;
  addressPostalAddressId: string;
  addresseeContactId: string;
};

export type CasusDebtorEmailAddress = {
  emailAddressId: string;
  emailContactId: string;
};

export type CasusDebtorInResponse = {
  casusDisplayNameWithClient: string;
  displayName: string;
  id: string;
};

export type CasusDisplayInformation = {
  casusNumber: string;
  displayNameWithClient: string;
  displayNameWithClientAndNumber: string;
  groupName?: string;
  id: string;
  name: string;
};

export type CasusNotInvoiceableCondition = {
  casusId: string;
  conditionType: 'CasusNotInvoiceableCondition';
};

export type ClientInResponse = {
  displayName: string;
  id: string;
};

export type ClientMoneyBooking = {
  accountingReceiptReference?: AccountingReceiptReferenceInResponse;
  amount: Money;
  bankAccountId?: string;
  bookingNumber: string;
  bookingType: 'DEPOSIT' | 'WITHDRAWAL' | 'TRANSFER_SOURCE' | 'TRANSFER_TARGET';
  createdAt: string;
  createdBy: UserInResponse;
  debtor: ClientMoneyDebtor;
  id: string;
  summary: string;
  valueDate: string;
};

export type ClientMoneyBookingCreateParams = {
  amount: Money;
  bankAccountId: string;
  debtorId: string;
  summary: string;
  valueDate: string;
};

export type ClientMoneyBookingCreateResponse = {
  id: string;
};

export type ClientMoneyBookingDeletionNotPossibleCondition = {
  casusUniqueIdentifier?: number;
  conditionType: string;
  reason:
    | 'REFERENCED_BY_FINANCE_EXPORT'
    | 'ACCOUNT_WITH_NEGATIVE_BALANCE'
    | 'COUNTER_BOOKING_INACCESSIBLE';
};

export type ClientMoneyBookingDetailResponse = {
  bookingInfo: ClientMoneyBooking;
  transferCounterBookingInfo?: ClientMoneyBooking;
};

export type ClientMoneyBookingsResponse = {
  clientMoneyBookings: PageClientMoneyBooking;
};

export type ClientMoneyDebtor = {
  balance: Money;
  casusDisplayName: string;
  casusDisplayNameWithNumber: string;
  casusId: string;
  casusNumber: string;
  debtorDisplayName: string;
  debtorId: string;
  id: string;
};

export type ClientMoneyDebtorCasusOverviewResponse = {
  balance: Money;
  nrOfDebtors: number;
};

export type ClientMoneyDebtorsResponse = {
  clientMoneyDebtors: PageClientMoneyDebtor;
};

export type ClientMoneyTransferCreateParams = {
  amount: Money;
  sourceDebtorId: string;
  summary: string;
  targetDebtorId: string;
  valueDate: string;
};

export type ClientMoneyTransferCreateResponse = {
  sourceBookingId: string;
  targetBookingId: string;
};

export type Contact = {
  displayName: string;
  id: string;
};

export type ContainerItemAmountBeforeTaxUpdateInParams = {
  amountBeforeTax: Money;
};

export type ContainerItemBillabilityUpdateParams = {
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
};

export type ContainerItemDurationUpdateInParams = {
  durationInSeconds: number;
};

export type ContainerItemNumberOfUnitsUpdateInParams = {
  numberOfUnits: number;
};

export type ContainerItemRateUpdateParams = {
  rate: Money;
};

export type ContainerItemTaxAmountUpdateInParams = {
  taxAmount: Money;
};

export type ContainerItemTaxRateUpdateInParams = {
  taxRate: TaxRate;
};

export type ContainerItemsAttributeUpdateParams = {
  amountBeforeTax?: ContainerItemAmountBeforeTaxUpdateInParams;
  containerItemIds: Array<string>;
  duration?: ContainerItemDurationUpdateInParams;
  hourlyRate?: ContainerItemRateUpdateParams;
  numberOfUnits?: ContainerItemNumberOfUnitsUpdateInParams;
  rate?: ContainerItemRateUpdateParams;
  taxAmount?: ContainerItemTaxAmountUpdateInParams;
  taxRate?: ContainerItemTaxRateUpdateInParams;
};

export type ContainerItemsWithTotals = {
  containerPaymentItems: Array<ContainerPaymentItemInResponse>;
  filteredItems: Array<InvoiceContainerItemInResponse>;
  filteredSubtotal?: TotalAmounts;
  foreignAmounts: Array<TaxRateAmounts>;
  percentageItem?: PercentageContainerItemInResponse;
  recipientDeductibleInputTax: Array<RecipientDeductibleInputTax>;
  recipientOwedAmounts: Array<RecipientOwedAmount>;
  recipientTotals: Array<RecipientTotals>;
  taxRateAmounts: Array<TaxRateAmounts>;
  total?: TotalAmounts;
  totalDeductibleInputTax: Money;
  totalOwedAmount: Money;
  unfilteredItems: Array<InvoiceContainerItemInResponse>;
  unfilteredSubtotal?: TotalAmounts;
};

export type ContainerPaymentItemInParams = {
  description?: string;
  summary: string;
};

export type ContainerPaymentItemInResponse = {
  amountToBeConsumed?: Money;
  createdAt: string;
  id: string;
  paymentAmount: Money;
  paymentRemainingAmount: Money;
  recipientDisplayName: string;
  summary: string;
  tax: Money;
  taxReduction?: Money;
  valueDate: string;
  vatNumberId: string;
};

export type CreateAdvancePaymentRequestContainerResponse = {
  createdAdvancePaymentInvoiceContainerId: string;
};

export type CreateInvoiceContainersResponse = {
  createdInvoiceContainerIds: Array<string>;
};

export type CreateInvoiceEmailResponse = {
  temporaryDocumentEmlTemplateId?: string;
};

export type CreateInvoiceReminderResponse = {
  createdInvoiceReminderId: string;
};

export type CreatePaymentParams = {
  amount: Money;
  assignmentParams: AssignmentParams;
  bankAccountId: string;
  casusDebtorId: string;
  valueDate: string;
};

export type CreatePaymentResponse = {
  createdPaymentId: string;
};

export type CreateTemporaryDocumentResponse = {
  temporaryDocumentId: string;
};

export type CreateTemporaryInvoiceReminderDocumentFromTemplateParams = {
  casusId: string;
  datePlaceholders: Array<DatePlaceholderInParams>;
  dueDate: string;
  entityPlaceholders: Array<EntityPlaceholderInParams>;
  locale: string;
  numberPlaceholders: Array<NumberPlaceholderInParams>;
  reminderDate: string;
  stringPlaceholders: Array<StringPlaceholderInParams>;
  templateFileId: string;
  templateId: string;
};

export type CreateTransferParams = {
  amount: Money;
  assignmentParams: AssignmentParams;
  casusDebtorId: string;
  valueDate: string;
};

export type DatePlaceholderInParams = {
  key: string;
  type: 'DATE';
  value?: string;
};

export type DayBasedPosition = {
  activityId?: string;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  casusDisplayNameWithClient: string;
  casusId: string;
  feeId: string;
  feeType:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
  invoiced: boolean;
  reportedTotals: ReportingValues;
  summary: string;
};

export type DayBasedReport = {
  positions: Array<DayBasedPosition>;
  totals: ReportingValues;
};

export type DebtorAttributes = {
  displayName: string;
};

export type DefaultBankAccountResponse = {
  bankAccountId?: string;
};

export type DefaultValueInResponse = {
  entityId?: string;
  options?: Array<ValueOptionInResponse>;
  value: string;
};

export type DeleteContainerItemsParams = {
  itemIds: Array<string>;
};

export type DeleteFeesInParams = {
  feeIds: Array<string>;
};

export type EntityPlaceholderInParams = {
  identifier: Array<string>;
  key: string;
  type:
    | 'CONTACT'
    | 'COLLABORATOR'
    | 'INVOLVED_PARTY'
    | 'CASUS'
    | 'CLIENT'
    | 'USER'
    | 'OFFICE_LOCATION'
    | 'CLIENT_MONEY_BANK_ACCOUNT'
    | 'SPECIALITY_FIELD';
};

export type EnumOptionInResponse = {
  categoryName?: string;
  displayName: string;
  value: string;
};

export type ExpenseContainerItemInParams = {
  amountPerUnitBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  invoiceRecipientId?: string;
  numberOfUnits: number;
  summary: string;
  targetCasusId: string;
  taxRate: TaxRate;
};

export type ExpenseFeeInParams = {
  amountPerUnitBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  casusDebtorId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  numberOfUnits: number;
  summary: string;
  targetCasusId: string;
  taxRate: TaxRate;
};

export type ExpenseFeeInResponse = {
  amountPerUnit: Money;
  casusDebtorId?: string;
  numberOfUnits: number;
  taxRate: TaxRate;
};

export type ExpenseInvoiceContainerItemInResponse = {
  amountPerUnit: Money;
  invoiceRecipientId?: string;
  numberOfUnits: number;
  taxRate: TaxRate;
};

export type ExternalExpenseByAbsoluteContainerItemInParams = {
  absoluteTaxAmount: Money;
  amountBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  invoiceRecipientId?: string;
  summary: string;
  targetCasusId: string;
};

export type ExternalExpenseByAbsoluteFeeInParams = {
  absoluteTaxAmount: Money;
  amountBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  casusDebtorId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
};

export type ExternalExpenseByAbsoluteFeeInResponse = {
  amountBeforeTax: Money;
  casusDebtorId?: string;
  taxAmount: Money;
};

export type ExternalExpenseByAbsoluteInvoiceContainerItemInResponse = {
  amountBeforeTax: Money;
  invoiceRecipientId?: string;
  taxAmount: Money;
};

export type ExternalExpenseByRateContainerItemInParams = {
  amountPerUnitBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  invoiceRecipientId?: string;
  summary: string;
  targetCasusId: string;
  taxRate: TaxRate;
};

export type ExternalExpenseByRateFeeInParams = {
  amountPerUnitBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  casusDebtorId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
  taxRate: TaxRate;
};

export type ExternalExpenseByRateFeeInResponse = {
  amountPerUnit: Money;
  casusDebtorId?: string;
  taxRate: TaxRate;
};

export type ExternalExpenseByRateInvoiceContainerItemInResponse = {
  amountPerUnit: Money;
  invoiceRecipientId?: string;
  taxRate: TaxRate;
};

export type ExternalExpenseItemStatistic = {
  feeProviderUser: UserInResponse;
  totalAfterTax: Money;
};

export type ExternalExpenseItemStatistics = {
  itemData: Array<ExternalExpenseItemStatistic>;
  totalAfterTax: Money;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type FeeBillabilityUpdateInParams = {
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
};

export type FeeHourlyRateUpdateInParams = {
  hourlyRate: Money;
};

export type FeeInResponse = {
  activityFeeData?: ActivityFeeInResponse;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  expenseFeeData?: ExpenseFeeInResponse;
  externalExpenseByAbsoluteFeeData?: ExternalExpenseByAbsoluteFeeInResponse;
  externalExpenseByRateFeeData?: ExternalExpenseByRateFeeInResponse;
  feeProviderUser: UserInResponse;
  flatFeeData?: FlatFeeInResponse;
  hourlyFeeData?: HourlyFeeInResponse;
  id: string;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  tax?: Money;
  taxRate?: TaxRate;
  totalAfterTax?: Money;
  totalBeforeTax: Money;
  totalInvoiceableBeforeTax: Money;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type FeeInUseCondition = {
  conditionType: 'FeeInUseCondition';
  containerId: string;
  feeId: string;
};

export type FeePeriodDetailPositions = {
  date?: string;
  total: ReportingValues;
};

export type FeePeriodReport = {
  casusDisplayNameWithClient: string;
  details: Array<FeePeriodDetailPositions>;
  reportedTotals: ReportingValues;
};

export type FeeSummary = {
  rateAfter?: Money;
  rateBefore?: Money;
  seconds?: number;
  totalAfter: Money;
  totalBefore: Money;
};

export type FeeTaxRateUpdateInParams = {
  taxRate: TaxRate;
};

export type FeeTotal = {
  totalAfter: Money;
  totalBefore: Money;
};

export type FeesAttributeUpdateParams = {
  billability?: FeeBillabilityUpdateInParams;
  feeIds: Array<string>;
  hourlyRate?: FeeHourlyRateUpdateInParams;
  taxRate?: FeeTaxRateUpdateInParams;
};

export type FeesResponse = {
  fees: Array<FeeInResponse>;
  totalBeforeTax: Money;
  totalHours: number;
  totalInvoiceableBeforeTax: Money;
};

export type FlatContainerItemInParams = {
  amount: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  percentageItemDisabled: boolean;
  summary: string;
  targetCasusId: string;
};

export type FlatFeeInParams = {
  amount: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  feeProviderUserId: string;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
};

export type FlatFeeInResponse = {
  amountBeforeTax: Money;
};

export type FlatInvoiceContainerItemInResponse = {
  amountBeforeTax: Money;
  percentageItemDisabled: boolean;
};

export type GetAdvancedPaymentRequestDefaultsResponse = {
  defaultDueDate: string;
  invoiceDate: string;
};

export type GetAllContactsResponse = {
  contacts: PageContact;
};

export type GetBillableCasusResponse = {
  billableCasus: Array<BillableCasusInResponse>;
};

export type GetBookingsResponse = {
  bookings: Array<Booking>;
};

export type GetCasusClientsResponse = {
  clients: PageClientInResponse;
};

export type GetCasusDebtorsResponse = {
  casusDebtors: Array<CasusDebtorInResponse>;
};

export type GetInvoiceDefaultsResponse = {
  defaultCompleteByDeadlineDate: string;
  defaultInvoiceDueDays: number;
  fromInclusive?: string;
  invoiceDate: string;
  toInclusive?: string;
};

export type GetInvoiceReminderDefaultsResponse = {
  defaultDueDate: string;
  reminderDate: string;
};

export type GetInvoiceRemindersResponse = {
  reminders: Array<InvoiceReminder>;
};

export type GetOverridableTemplatePlaceholdersResponse = {
  pagedPlaceholders: Array<PageInResponse>;
};

export type GetPaymentAssignmentCandidatesResponse = {
  assignablePayments: Array<AssignmentPayment>;
  assignedPayments: Array<AssignmentPayment>;
};

export type GetPaymentsResponse = {
  payments: PagePayment;
};

export type GetTemplatesResponse = {
  templates: Array<TemplateInResponse>;
};

export type GroupHourlyRate = {
  group: GroupInResponse;
  hourlyRate: HourlyRate;
};

export type GroupInResponse = {
  id: string;
  name: string;
};

export type GroupWithAllHourlyRates = {
  casusGroupSpecificHourlyRate?: HourlyRate;
  casusSpecificHourlyRate?: HourlyRate;
  group: GroupInResponse;
  groupSpecificHourlyRate?: HourlyRate;
};

export type HasExportedReceiptsResponse = {
  hasExportedReceipt: boolean;
};

export type HasInvoicesInStateDoneResponse = {
  hasInvoicesInStateDone: boolean;
};

export type HourlyFeeInParams = {
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  durationInSeconds: number;
  feeProviderUserId: string;
  hourlyRate: Money;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
};

export type HourlyFeeInResponse = {
  duration: number;
  hourlyRate: Money;
};

export type HourlyInvoiceContainerItemInResponse = {
  duration: number;
  hourlyRate: Money;
};

export type HourlyRate = {
  amount: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
};

export type ImportTransactionSetEndpointCondition = {
  conditionType:
    | 'Camt054WithUnknownIbanCondition'
    | 'Camt054WithoutIbanCondition';
};

export type ImportTransactionSetResponse = {
  importedTransactionCount: number;
  invalidTransactionsCount: number;
  totalTransactionCount: number;
};

export type Invoice = {
  accountingReceiptReference?: AccountingReceiptReferenceInResponse;
  cancellation?: Cancellation;
  casus: Casus;
  containerId: string;
  createdAt: string;
  createdBy: UserInResponse;
  id: string;
  invoiceDate: string;
  invoiceDocument?: InvoiceDocumentInResponse;
  invoiceDueDate: string;
  invoiceNumber: string;
  invoiceRecipientDisplayName: string;
  owedAmount: Money;
  remainingAmount: Money;
  reminderDate?: string;
  reminderDueDate?: string;
  reminderState?: 'SENT' | 'OVERDUE' | 'DONE';
  state: 'SENT' | 'OVERDUE' | 'DONE' | 'CANCELLED';
  totalAmountAfterTax: Money;
  type: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
};

export type InvoiceAssignment = {
  invoiceId: string;
  invoiceType: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
  remainingAmount: Money;
};

export type InvoiceAttributes = {
  invoiceNumber: string;
  invoiceRecipientDisplayName: string;
  invoiceType: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
  remainingAmount: Money;
  totalAmountAfterTax: Money;
  vatNumberId?: string;
};

export type InvoiceContainerChangeTotalFeeSummary = {
  expenseItems: Money;
  externalExpenseItems: Money;
  flatItems: Money;
  hourlyItems: Money;
  percentageItem?: PercentageItem;
};

export type InvoiceContainerChangeTotalSummary = {
  bestApproximationTotalAfterTax: Money;
  bestApproximationTotalBeforeTax: Money;
  feeSummary: InvoiceContainerChangeTotalFeeSummary;
  feeTotal: FeeTotal;
  fees: Array<FeeSummary>;
  residual?: Money;
  resultTotalAfterTax: Money;
  resultTotalBeforeTax: Money;
  roundingDifference?: Money;
  totalWithoutResidual: Money;
  totalWithoutRoundingDifference: Money;
};

export type InvoiceContainerInResponse = {
  cancellationContainerId?: string;
  cancellationDate?: string;
  casus: Casus;
  casusDefaultPercentageFeeAsDecimal?: string;
  completeByDueDate?: string;
  createdAt: string;
  creator: UserInResponse;
  feeCompleterUserDisplayNames: Array<string>;
  id: string;
  invoiceContainerNumber: string;
  invoiceDate: string;
  invoiceDueDate: string;
  periodFromInclusive: string;
  periodToInclusive: string;
  recipients: Array<InvoiceRecipientInResponse>;
  signatoryUsers: Array<UserInResponse>;
  state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
  templateFileDisplayName?: string;
  templateFileId?: string;
  type: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
  vatNumberId?: string;
};

export type InvoiceContainerItemInResponse = {
  activityData?: ActivityInvoiceContainerItemInResponse;
  advancePaymentData?: AdvancePaymentInvoiceContainerItemInResponse;
  amountAfterTax?: Money;
  amountBeforeTax: Money;
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  expenseData?: ExpenseInvoiceContainerItemInResponse;
  externalExpenseByAbsoluteData?: ExternalExpenseByAbsoluteInvoiceContainerItemInResponse;
  externalExpenseByRateData?: ExternalExpenseByRateInvoiceContainerItemInResponse;
  flatData?: FlatInvoiceContainerItemInResponse;
  hourlyData?: HourlyInvoiceContainerItemInResponse;
  id: string;
  incurredAt: string;
  internalNote?: string;
  itemOwner: UserInResponse;
  recipients: Array<InvoiceContainerItemRecipientInResponse>;
  summary: string;
  tax?: Money;
  taxRate?: TaxRate;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
  wasCreatedFromFee: boolean;
};

export type InvoiceContainerItemRecipientInResponse = {
  amountAfterTax?: Money;
  amountBeforeTax?: Money;
  displayName: string;
  fractionAsDecimal: string;
  id: string;
  tax?: Money;
  taxRate?: TaxRate;
};

export type InvoiceContainerItemStatisticsResponse = {
  expenseItems: ItemStatistics;
  externalExpenseByAbsoluteItems: ExternalExpenseItemStatistics;
  externalExpenseByRateItems: ItemStatistics;
  flatItems: ItemStatistics;
  timeBasedItems: TimeBasedItemStatistics;
};

export type InvoiceContainerOverviewInResponse = {
  cancellationContainerId?: string;
  cancellationDate?: string;
  casus: Casus;
  createdAt: string;
  createdBy: UserInResponse;
  id: string;
  invoiceContainerNumber: string;
  invoiceDate: string;
  owedAmount?: Money;
  periodFromInclusive: string;
  periodToInclusive: string;
  recipientDisplayNames: Array<string>;
  state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
  totalAfterTax?: Money;
  type: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
};

export type InvoiceContainerPercentageFeeUpdateParams = {
  percentageFeeAsDecimal: string;
};

export type InvoiceContainerResponse = {
  invoiceContainer: InvoiceContainerInResponse;
};

export type InvoiceContainerSettingsUpdateParams = {
  invoiceDate: string;
  invoiceDueDate: string;
  periodFromInclusive: string;
  periodToInclusive: string;
  signatoryUserIds: Array<string>;
  templateFileId?: string;
  vatNumberId?: string;
};

export type InvoiceContainersCreateParams = {
  casusIds: Array<string>;
  completeByDueDate?: string;
  feeIds?: Array<string>;
  feeProviderUserIds?: Array<string>;
  fromInclusive: string;
  invoiceDate: string;
  invoiceDueDate: string;
  positionTypes?: Array<
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT'
  >;
  toInclusive: string;
};

export type InvoiceContainersDeleteParams = {
  containerIds: Array<string>;
};

export type InvoiceContainersResponse = {
  invoiceContainers: PageInvoiceContainerOverviewInResponse;
};

export type InvoiceDocumentIdInResponse = {
  documentId: string;
};

export type InvoiceDocumentInResponse = {
  documentId: string;
  recipientDisplayName: string;
};

export type InvoiceDocumentsResponse = {
  invoiceDocuments: Array<InvoiceDocumentIdInResponse>;
};

export type InvoiceItemDetails = {
  percentageTotals?: TotalAmounts;
  positionItems: Array<InvoicePositionItem>;
  positionTotals: TotalAmounts;
  taxPerRate: Array<TaxRateAmounts>;
  totals: TotalAmounts;
};

export type InvoicePositionItem = {
  amountAfterTax: Money;
  amountBeforeTax: Money;
  amountPerUnit?: Money;
  billableCategoryId?: string;
  duration?: number;
  feeProvider: UserInResponse;
  gifted: boolean;
  hourlyRate?: Money;
  id: string;
  incurredAt: string;
  numberOfUnits?: number;
  summary?: string;
  taxAmount: Money;
  taxRate?: TaxRate;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type InvoiceRecipientFractionsUpdateParams = {
  recipientFractions: Array<RecipientFraction>;
};

export type InvoiceRecipientInResponse = {
  bankAccountId: string;
  casusDebtorId: string;
  contactIds: CasusDebtorContactIds;
  deliveryContactIds?: CasusDebtorContactIds;
  displayName: string;
  domesticallyBased: boolean;
  emailAddress?: CasusDebtorEmailAddress;
  formattedAddress: string;
  formattedDeliveryAddress?: string;
  formattedEmailAddress?: string;
  fractionAsDecimal: string;
  hasOverdueInvoices: boolean;
  id: string;
  invoiceId?: string;
  invoiceNumber?: string;
  remainingAmount?: Money;
  taxRate?: TaxRate;
};

export type InvoiceRecipientStoringParams = {
  bankAccountId: string;
  contactIds: CasusDebtorContactIds;
  deliveryContactIds?: CasusDebtorContactIds;
  domesticallyBased: boolean;
  emailAddress?: CasusDebtorEmailAddress;
  taxRate?: TaxRate;
};

export type InvoiceReminder = {
  document: InvoiceReminderDocument;
  dueDate: string;
  id: string;
  reminderDate: string;
  reminderNumber: number;
  state: 'SENT' | 'OVERDUE' | 'DONE';
  totalAmountAfterTax: Money;
};

export type InvoiceReminderCreateParams = {
  casusId: string;
  datePlaceholders: Array<DatePlaceholderInParams>;
  dueDate: string;
  entityPlaceholders: Array<EntityPlaceholderInParams>;
  locale: string;
  numberPlaceholders: Array<NumberPlaceholderInParams>;
  reminderDate: string;
  stringPlaceholders: Array<StringPlaceholderInParams>;
  templateFileId: string;
  templateId: string;
};

export type InvoiceReminderDocument = {
  documentId: string;
  invoiceReminderId: string;
  title: string;
};

export type InvoiceResponse = {
  invoice: Invoice;
  recipient: InvoiceRecipientInResponse;
};

export type InvoicesResponse = {
  invoices: Array<Invoice>;
};

export type InvoicingMasterData = {
  allTranslatedBankAccounts: Array<TranslatedBankAccount>;
  hourlyRates: Array<Money>;
  quickFeeActivities: Array<QuickFeeActivity>;
  taxes: Array<Tax>;
  translatedBankAccounts: Array<TranslatedBankAccount>;
  translatedBillableCategories: Array<TranslatedBillableCategory>;
  translatedClientMoneyBankAccounts: Array<TranslatedBankAccount>;
  translatedVatNumbers: Array<TranslatedVatNumber>;
};

export type InvoicingTransactionCandidateAddress = {
  address?: string;
  city?: string;
  countryCode?: string;
  postCode?: string;
};

export type IsClientMoneyBookingDeletionPossible = {
  casusUniqueIdentifier?: number;
  reasonIfNotPossible?:
    | 'REFERENCED_BY_FINANCE_EXPORT'
    | 'ACCOUNT_WITH_NEGATIVE_BALANCE'
    | 'COUNTER_BOOKING_INACCESSIBLE';
};

export type IsPaymentBookingGroupDeletionPossible = {
  reasonIfNotPossible?:
    | 'INVALID_STATE'
    | 'NOT_SUPPORTED'
    | 'REFERENCED_BY_FINANCE_EXPORT';
};

export type IsPaymentDeletionPossible = {
  reasonIfNotPossible?:
    | 'INVALID_STATE'
    | 'NOT_REVOKED'
    | 'REFERENCED_BY_FINANCE_EXPORT'
    | 'ASSIGNED'
    | 'HAS_REPAYMENT'
    | 'HAS_ROUNDING_DIFFERENCE'
    | 'HAS_TRANSFER'
    | 'NO_ACCESS_TO_SOURCE_PAYMENT'
    | 'TRANSFER_SOURCE_PAYMENT_ASSIGNED'
    | 'TRANSFER_SOURCE_PAYMENT_HAS_ADDITIONAL_BOOKINGS'
    | 'TRANSFER_SOURCE_BOOKING_REFERENCED_BY_FINANCE_EXPORT';
};

export type IsPaymentSettlementRevocationPossible = {
  defaultSummary?: string;
  reasonIfNotPossible?:
    | 'INVALID_STATE'
    | 'WRITE_OFF_OR_UNDERPAYMENT_ROUNDING_DIFFERENCE'
    | 'OVERPAYMENT_ROUNDING_DIFFERENCE'
    | 'ALREADY_REVOKED'
    | 'CANNOT_BE_REVOKED'
    | 'PREPAYMENT';
};

export type IsRoundOverpaymentPossible = {
  reasonIfNotPossible?:
    | 'UNEXPECTED_REMAINING_AMOUNT'
    | 'NO_INVOICE'
    | 'NO_UNCANCELLED_INVOICE';
};

export type IsWriteOffDeletionPossible = {
  reasonIfNotPossible?:
    | 'NO_WRITEOFF'
    | 'INVALID_STATE'
    | 'REFERENCED_BY_FINANCE_EXPORT';
};

export type ItemStatistic = {
  feeProviderUser: UserInResponse;
  totalBeforeTax: Money;
};

export type ItemStatistics = {
  itemData: Array<ItemStatistic>;
  totalBeforeTax: Money;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type Money = {
  amount: string;
};

export type MoveFeesInParams = {
  feeIds: Array<string>;
  targetCasusId: string;
};

export type NumberPlaceholderInParams = {
  key: string;
  type: 'NUMBER';
  value?: number;
};

export type PageCasusDebtorInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemCasusDebtorInResponse>;
};

export type PageClientInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemClientInResponse>;
};

export type PageClientMoneyBooking = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemClientMoneyBooking>;
};

export type PageClientMoneyDebtor = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemClientMoneyDebtor>;
};

export type PageContact = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemContact>;
};

export type PageInResponse = {
  key: string;
  label: string;
  placeholders: Array<PlaceholderInResponse>;
};

export type PageInvoice = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemInvoice>;
};

export type PageInvoiceContainerOverviewInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemInvoiceContainerOverviewInResponse>;
};

export type PageItemCasusDebtorInResponse = {
  cursor: string;
  item: CasusDebtorInResponse;
};

export type PageItemClientInResponse = {
  cursor: string;
  item: ClientInResponse;
};

export type PageItemClientMoneyBooking = {
  cursor: string;
  item: ClientMoneyBooking;
};

export type PageItemClientMoneyDebtor = {
  cursor: string;
  item: ClientMoneyDebtor;
};

export type PageItemContact = {
  cursor: string;
  item: Contact;
};

export type PageItemInvoice = {
  cursor: string;
  item: Invoice;
};

export type PageItemInvoiceContainerOverviewInResponse = {
  cursor: string;
  item: InvoiceContainerOverviewInResponse;
};

export type PageItemPayment = {
  cursor: string;
  item: Payment;
};

export type PageItemTransactionAssignmentCandidate = {
  cursor: string;
  item: TransactionAssignmentCandidate;
};

export type PageItemTransactionSetOverview = {
  cursor: string;
  item: TransactionSetOverview;
};

export type PageItemUserInResponse = {
  cursor: string;
  item: UserInResponse;
};

export type PageItemUserWithHourlyRate = {
  cursor: string;
  item: UserWithHourlyRate;
};

export type PagePayment = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemPayment>;
};

export type PageTransactionAssignmentCandidate = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemTransactionAssignmentCandidate>;
};

export type PageTransactionSetOverview = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemTransactionSetOverview>;
};

export type PageUserInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemUserInResponse>;
};

export type PageUserWithHourlyRate = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemUserWithHourlyRate>;
};

export type PagedInvoicesResponse = {
  invoices: PageInvoice;
};

export type Payment = {
  accountingReceiptReference?: AccountingReceiptReferenceInResponse;
  bankAccountId?: string;
  casus: CasusDisplayInformation;
  createdAt: string;
  createdBy: UserInResponse;
  debtorDisplayName: string;
  id: string;
  paymentNumber: string;
  paymentSource: 'IMPORT' | 'MANUAL' | 'TRANSFER';
  prepaymentType?: 'PREPAYMENT' | 'RETAINER';
  referenceNumber?: string;
  remainingAmount: Money;
  state: 'OPEN' | 'CONSUMED';
  summary: string;
  totalAmount: Money;
  transferSourcePaymentId?: string;
  valueDate: string;
  vatNumber: TranslatedVatNumber;
};

export type PaymentBookingGroupDeletionNotPossibleCondition = {
  conditionType: string;
  reason: 'INVALID_STATE' | 'NOT_SUPPORTED' | 'REFERENCED_BY_FINANCE_EXPORT';
};

export type PaymentCondition = {
  conditionType:
    | 'PaymentConsumedByTemporaryInvoiceCondition'
    | 'UnexpectedPaymentRemainingAmountCondition'
    | 'RoundOverpaymentWithoutInvoiceCondition'
    | 'RoundOverpaymentWithoutUncancelledInvoiceCondition';
};

export type PaymentConsumedByTemporaryInvoiceCondition = {
  conditionType: 'PaymentConsumedByTemporaryInvoiceCondition';
  containerId: string;
  uniqueIdentifier: number;
};

export type PaymentDeletionNotPossibleCondition = {
  conditionType: string;
  reason:
    | 'INVALID_STATE'
    | 'NOT_REVOKED'
    | 'REFERENCED_BY_FINANCE_EXPORT'
    | 'ASSIGNED'
    | 'HAS_REPAYMENT'
    | 'HAS_ROUNDING_DIFFERENCE'
    | 'HAS_TRANSFER'
    | 'NO_ACCESS_TO_SOURCE_PAYMENT'
    | 'TRANSFER_SOURCE_PAYMENT_ASSIGNED'
    | 'TRANSFER_SOURCE_PAYMENT_HAS_ADDITIONAL_BOOKINGS'
    | 'TRANSFER_SOURCE_BOOKING_REFERENCED_BY_FINANCE_EXPORT';
};

export type PaymentRevocationParams = {
  summary: string;
  valueDate: string;
};

export type PaymentSettlementRevocationNotPossibleCondition = {
  conditionType: string;
  reason:
    | 'INVALID_STATE'
    | 'WRITE_OFF_OR_UNDERPAYMENT_ROUNDING_DIFFERENCE'
    | 'OVERPAYMENT_ROUNDING_DIFFERENCE'
    | 'ALREADY_REVOKED'
    | 'CANNOT_BE_REVOKED'
    | 'PREPAYMENT';
};

export type PdfReceiptParams = {
  id: string;
  type: 'PAYMENT' | 'BOOKING_GROUP' | 'WRITE_OFF' | 'CLIENT_MONEY';
};

export type PercentageContainerItemInResponse = {
  amountAfterTax?: Money;
  amountBeforeTax: Money;
  percentageAsDecimal: string;
  recipients: Array<InvoiceContainerItemRecipientInResponse>;
  tax?: Money;
  taxRate?: TaxRate;
};

export type PercentageItem = {
  amount: Money;
  percentage: string;
};

export type PeriodReport = {
  positions: Array<FeePeriodReport>;
  totals: ReportingValues;
};

export type PlaceholderInResponse = {
  defaultValue: Array<DefaultValueInResponse>;
  editFieldType?: 'TEXT' | 'TEXTAREA';
  enumOptions?: Array<EnumOptionInResponse>;
  key: string;
  label: string;
  multiple: boolean;
  optional: boolean;
  placeholders?: Array<PlaceholderInResponse>;
  type:
    | 'ENUM'
    | 'USER_AND_ROLE'
    | 'USER_AND_CONTACT_ROLE'
    | 'BILLABLE_CATEGORY'
    | 'LOCALE'
    | 'TEXT'
    | 'TEXTAREA'
    | 'NUMBER'
    | 'DATE'
    | 'CONTACT'
    | 'COLLABORATOR'
    | 'INVOLVED_PARTY'
    | 'CASUS'
    | 'CLIENT'
    | 'USER'
    | 'OFFICE_LOCATION'
    | 'CLIENT_MONEY_BANK_ACCOUNT'
    | 'SPECIALITY_FIELD'
    | 'EMAIL'
    | 'PHONE'
    | 'ADDRESS'
    | 'LINK'
    | 'GROUP';
};

export type PrepaymentParams = {
  includingTax: boolean;
  retainer: boolean;
  summary: string;
  valueDate: string;
  vatNumberId: string;
};

export type QuickFeeActivity = {
  defaultAmount?: Money;
  id: string;
  kind: 'BILLABLE_CATEGORY' | 'TEXT_FRAGMENT';
  shortcut?: string;
  text: string;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type QuickFeeDurationParseCondition = {
  conditionType: 'QuickFeeDurationParseCondition';
  input?: string;
};

export type QuickFeeMultiplierParseCondition = {
  conditionType: 'QuickFeeMultiplierParseCondition';
  input?: string;
};

export type QuickFeeParseCondition = {
  conditionType:
    | 'QuickFeeDurationParseCondition'
    | 'QuickFeeMultiplierParseCondition';
};

export type RecipientDeductibleInputTax = {
  deductibleInputTax?: Money;
  recipientDisplayName: string;
};

export type RecipientFraction = {
  fractionAsDecimal: string;
  invoiceRecipientId: string;
};

export type RecipientOwedAmount = {
  owedAmount?: Money;
  recipientDisplayName: string;
};

export type RecipientTotals = {
  fractionAsDecimal: string;
  recipientDisplayName: string;
  tax?: Money;
  totalAfterTax?: Money;
  totalBeforeTax?: Money;
};

export type RelatedAddressContactInResponse = {
  label: string;
  roleId: string;
};

export type RepayPaymentParams = {
  amount: Money;
  bankAccountId: string;
  summary: string;
  valueDate: string;
};

export type RepaymentParams = {
  bankAccountId: string;
  summary: string;
  valueDate: string;
  vatNumberId: string;
};

export type ReplacePaymentItemsParams = {
  paymentIds: Array<string>;
};

export type ReportingValues = {
  duration?: number;
  flatFee?: Money;
  hourlyFee?: Money;
  total?: Money;
};

export type RoundOverpaymentParams = {
  amount: Money;
  summary: string;
  valueDate: string;
};

export type RoundOverpaymentWithoutInvoiceCondition = {
  conditionType: 'RoundOverpaymentWithoutInvoiceCondition';
};

export type RoundOverpaymentWithoutUncancelledInvoiceCondition = {
  conditionType: 'RoundOverpaymentWithoutUncancelledInvoiceCondition';
};

export type RoundUnderpaymentParams = {
  amount: Money;
  summary: string;
  valueDate: string;
};

export type RoundingDifferenceParams = {
  summary: string;
  valueDate: string;
};

export type SearchCasusDebtorsResponse = {
  casusDebtors: PageCasusDebtorInResponse;
};

export type SearchCasusUsersResponse = {
  users: PageUserWithHourlyRate;
};

export type SearchItemOwnersResponse = {
  itemOwners: PageUserInResponse;
};

export type StateUpdateCheckResponse = {
  conflictingInvoiceContainers: Array<string>;
  conformingInvoiceContainers: Array<string>;
};

export type StateUpdateParams = {
  state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
};

export type StateUpdateResponse = {
  invoiceContainers: Array<string>;
};

export type StringPlaceholderInParams = {
  identifier?: string;
  key: string;
  type:
    | 'TEXT'
    | 'TEXTAREA'
    | 'ENUM'
    | 'USER_AND_ROLE'
    | 'USER_AND_CONTACT_ROLE'
    | 'BILLABLE_CATEGORY'
    | 'LOCALE'
    | 'EMAIL'
    | 'PHONE'
    | 'ADDRESS'
    | 'LINK';
  value: Array<string>;
};

export type Tax = {
  active: boolean;
  key: TaxKey;
  taxRate: TaxRate;
};

export type TaxKey = {
  code: string;
};

export type TaxRate = {
  ratioAsDecimal: string;
};

export type TaxRateAmounts = {
  tax: Money;
  taxRate?: TaxRate;
  totalAfterTax: Money;
  totalBeforeTax: Money;
};

export type TemplateFileInResponse = {
  id: string;
  locale: string;
  title: string;
};

export type TemplateInResponse = {
  id: string;
  templateFiles: Array<TemplateFileInResponse>;
  title: string;
};

export type TimeBasedItemStatistic = {
  duration: number;
  feeProviderUser: UserInResponse;
  hourlyRate: Money;
  totalBeforeTax: Money;
};

export type TimeBasedItemStatistics = {
  timeBasedItemData: Array<TimeBasedItemStatistic>;
  totalBeforeTax: Money;
  type:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
};

export type TimebasedContainerItemInParams = {
  billability: 'DO_INVOICE' | 'DO_NOT_INVOICE' | 'GIFT';
  billableCategoryId?: string;
  description?: string;
  durationInSeconds: number;
  feeProviderUserId: string;
  hourlyRate: Money;
  incurredAt: string;
  internalNote?: string;
  summary: string;
  targetCasusId: string;
};

export type TotalAmounts = {
  tax: Money;
  totalAfterTax: Money;
  totalBeforeTax: Money;
};

export type TransactionAssignmentCandidate = {
  address: InvoicingTransactionCandidateAddress;
  bankAccountId: string;
  casusDebtorId: string;
  casusDisplayNameWithClient: string;
  casusDisplayNameWithClientAndNumber: string;
  casusNumber: string;
  debtorAttributes?: DebtorAttributes;
  debtorIncludingTaxDiffers: boolean;
  fileNumber?: string;
  id: string;
  includingTax: boolean;
  invoiceAttributes?: InvoiceAttributes;
  procedureNumber?: string;
};

export type TransactionAssignmentCandidatesResponse = {
  items: PageTransactionAssignmentCandidate;
};

export type TransactionInResponse = {
  amount: Money;
  assignedInvoices: Array<AssignedInvoice>;
  bookingDate?: string;
  casusDisplayNameWithClient?: string;
  casusUniqueIdentifier?: number;
  id: string;
  payer?: string;
  referenceNumber?: string;
  summary?: string;
  valueDate: string;
};

export type TransactionSetOverview = {
  bankAccountId?: string;
  createdAt: string;
  creator: UserInResponse;
  doneTransactionCount: number;
  iban?: string;
  id: string;
  periodFromInclusive?: string;
  periodToInclusive?: string;
  state: 'UPLOADED' | 'IN_PROGRESS' | 'DONE';
  total: Money;
  transactionCount: number;
};

export type TransactionSetTransactionsResponse = {
  assignedTransactions: Array<TransactionInResponse>;
  duplicateTransactions: Array<TransactionInResponse>;
  ignoredTransactions: Array<TransactionInResponse>;
  matchedTransactions: Array<TransactionInResponse>;
  unmatchedTransactions: Array<TransactionInResponse>;
};

export type TransactionSetsResponse = {
  items: PageTransactionSetOverview;
};

export type TranslatedBankAccount = {
  active: boolean;
  id: string;
  kvgDefault: boolean;
  translatedName: string;
  type: 'STANDARD' | 'CLIENT_MONEY';
};

export type TranslatedBillableCategory = {
  active: boolean;
  defaultAmount?: Money;
  id: string;
  positionType:
    | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
    | 'EXTERNAL_EXPENSE_BY_RATE'
    | 'EXPENSE'
    | 'ACTIVITY'
    | 'HOURLY'
    | 'FLAT'
    | 'ADVANCE_PAYMENT';
  translatedName: string;
  translatedNameWithPositionType: string;
};

export type TranslatedVatNumber = {
  active: boolean;
  declarationMode: 'AGREED' | 'RECEIVED';
  id: string;
  translatedDescription: string;
  value: string;
};

export type UnexpectedInvoiceRemainingAmountCondition = {
  actualRemainingAmountAfterAssignment: Money;
  conditionType: 'UnexpectedInvoiceRemainingAmountCondition';
  expectedRemainingAmountAfterAssignment: Money;
  invoiceId: string;
};

export type UnexpectedPaymentRemainingAmountCondition = {
  conditionType: 'UnexpectedPaymentRemainingAmountCondition';
};

export type UnpaidInvoice = {
  casus: Casus;
  id: string;
  invoiceDate: string;
  invoiceDocument?: InvoiceDocumentInResponse;
  invoiceDueDate: string;
  invoiceNumber: string;
  remainingAmount: Money;
  totalAmountAfterTax: Money;
  type: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
  vatNumberId: string;
};

export type UpdateCancellationDateParams = {
  cancellationDate: string;
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type UserWithAllHourlyRates = {
  casusGroupRates: Array<GroupHourlyRate>;
  casusSpecificHourlyRate?: HourlyRate;
  casusUserSpecificHourlyRate?: HourlyRate;
  groupRates: Array<GroupHourlyRate>;
  user: UserInResponse;
  userSpecificHourlyRate?: HourlyRate;
};

export type UserWithHourlyRate = {
  hourlyRate: HourlyRate;
  user: UserInResponse;
};

export type ValueOptionInResponse = {
  classificationId?: string;
  displayName: string;
  entityId: string;
  fieldValue: string;
  relatedAddressContact?: RelatedAddressContactInResponse;
};

export type WriteOffDeletionNotPossibleCondition = {
  conditionType: string;
  reason: 'NO_WRITEOFF' | 'INVALID_STATE' | 'REFERENCED_BY_FINANCE_EXPORT';
};

export type WriteOffInvoiceParams = {
  amount: Money;
  summary: string;
  valueDate: string;
};

export type WriteOffParams = {
  summary: string;
  valueDate: string;
};

export type Response_getUnpaidInvoices_200 = Array<UnpaidInvoice>;

export type Response_downloadReport_200 = {};

export type Response_downloadExportAsPdf_200 = {};

export type Response_downloadInvoicePreviewPDF_200 = {};

export type Response_getCasusIdForPayment_200 = string;

export interface ResponseWithBody<S extends number, T> extends Response {
  status: S;
  body: T;
}

export type QueryParameters = {[param: string]: any};

export interface CommonRequestOptions {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
}

type SuperAgentError = {status: number};
const isSuperAgentError = (error: unknown): error is SuperAgentError =>
  Boolean((error as SuperAgentError).status);

/*
 *
 * @class Backend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class Backend {
  private domain: string = '';
  private onUnauthorizedHandler?: CallbackHandler;
  private errorHandlers: CallbackHandler[] = [];
  private requestHeadersHandler?: RequestHeadersHandler;
  private configureAgentHandler?: ConfigureAgentHandler;
  private configureRequestHandler?: ConfigureRequestHandler;

  public constructor(domain?: string, onUnauthorizedHandler?: CallbackHandler) {
    if (domain) {
      this.domain = domain;
    }

    if (onUnauthorizedHandler) {
      this.onUnauthorizedHandler = onUnauthorizedHandler;
    }

    this.createAdvancePaymentInvoiceContainerURL =
      this.createAdvancePaymentInvoiceContainerURL.bind(this);
    this.createAdvancePaymentInvoiceContainer =
      this.createAdvancePaymentInvoiceContainer.bind(this);
    this.getAdvancedPaymentRequestDefaultsURL =
      this.getAdvancedPaymentRequestDefaultsURL.bind(this);
    this.getAdvancedPaymentRequestDefaults =
      this.getAdvancedPaymentRequestDefaults.bind(this);
    this.getBillableCasusURL = this.getBillableCasusURL.bind(this);
    this.getBillableCasus = this.getBillableCasus.bind(this);
    this.getCasusClientsURL = this.getCasusClientsURL.bind(this);
    this.getCasusClients = this.getCasusClients.bind(this);
    this.searchCasusDebtorsURL = this.searchCasusDebtorsURL.bind(this);
    this.searchCasusDebtors = this.searchCasusDebtors.bind(this);
    this.getUnpaidInvoicesURL = this.getUnpaidInvoicesURL.bind(this);
    this.getUnpaidInvoices = this.getUnpaidInvoices.bind(this);
    this.getCasusDebtorsURL = this.getCasusDebtorsURL.bind(this);
    this.getCasusDebtors = this.getCasusDebtors.bind(this);
    this.getDefaultBankAccountIdURL =
      this.getDefaultBankAccountIdURL.bind(this);
    this.getDefaultBankAccountId = this.getDefaultBankAccountId.bind(this);
    this.getDefaultInvoiceRecipientsURL =
      this.getDefaultInvoiceRecipientsURL.bind(this);
    this.getDefaultInvoiceRecipients =
      this.getDefaultInvoiceRecipients.bind(this);
    this.deleteFeesURL = this.deleteFeesURL.bind(this);
    this.deleteFees = this.deleteFees.bind(this);
    this.getOpenFeesURL = this.getOpenFeesURL.bind(this);
    this.getOpenFees = this.getOpenFees.bind(this);
    this.addExpenseFeeURL = this.addExpenseFeeURL.bind(this);
    this.addExpenseFee = this.addExpenseFee.bind(this);
    this.updateExpenseFeeURL = this.updateExpenseFeeURL.bind(this);
    this.updateExpenseFee = this.updateExpenseFee.bind(this);
    this.addExternalExpenseByAbsoluteFeeURL =
      this.addExternalExpenseByAbsoluteFeeURL.bind(this);
    this.addExternalExpenseByAbsoluteFee =
      this.addExternalExpenseByAbsoluteFee.bind(this);
    this.updateExternalExpenseByAbsoluteFeeURL =
      this.updateExternalExpenseByAbsoluteFeeURL.bind(this);
    this.updateExternalExpenseByAbsoluteFee =
      this.updateExternalExpenseByAbsoluteFee.bind(this);
    this.addExternalExpenseByRateFeeURL =
      this.addExternalExpenseByRateFeeURL.bind(this);
    this.addExternalExpenseByRateFee =
      this.addExternalExpenseByRateFee.bind(this);
    this.updateExternalExpenseByRateFeeURL =
      this.updateExternalExpenseByRateFeeURL.bind(this);
    this.updateExternalExpenseByRateFee =
      this.updateExternalExpenseByRateFee.bind(this);
    this.addFlatFeeURL = this.addFlatFeeURL.bind(this);
    this.addFlatFee = this.addFlatFee.bind(this);
    this.updateFlatFeeURL = this.updateFlatFeeURL.bind(this);
    this.updateFlatFee = this.updateFlatFee.bind(this);
    this.addHourlyFeeURL = this.addHourlyFeeURL.bind(this);
    this.addHourlyFee = this.addHourlyFee.bind(this);
    this.updateHourlyFeeURL = this.updateHourlyFeeURL.bind(this);
    this.updateHourlyFee = this.updateHourlyFee.bind(this);
    this.moveFeesURL = this.moveFeesURL.bind(this);
    this.moveFees = this.moveFees.bind(this);
    this.addQuickFeeURL = this.addQuickFeeURL.bind(this);
    this.addQuickFee = this.addQuickFee.bind(this);
    this.setFeesAttributeURL = this.setFeesAttributeURL.bind(this);
    this.setFeesAttribute = this.setFeesAttribute.bind(this);
    this.getFeeURL = this.getFeeURL.bind(this);
    this.getFee = this.getFee.bind(this);
    this.duplicateFeeURL = this.duplicateFeeURL.bind(this);
    this.duplicateFee = this.duplicateFee.bind(this);
    this.getGroupWithAllPossibleRatesURL =
      this.getGroupWithAllPossibleRatesURL.bind(this);
    this.getGroupWithAllPossibleRates =
      this.getGroupWithAllPossibleRates.bind(this);
    this.getUserWithAllPossibleRatesURL =
      this.getUserWithAllPossibleRatesURL.bind(this);
    this.getUserWithAllPossibleRates =
      this.getUserWithAllPossibleRates.bind(this);
    this.searchCasusUsersURL = this.searchCasusUsersURL.bind(this);
    this.searchCasusUsers = this.searchCasusUsers.bind(this);
    this.getClientMoneyBookingsURL = this.getClientMoneyBookingsURL.bind(this);
    this.getClientMoneyBookings = this.getClientMoneyBookings.bind(this);
    this.createClientMoneyBookingURL =
      this.createClientMoneyBookingURL.bind(this);
    this.createClientMoneyBooking = this.createClientMoneyBooking.bind(this);
    this.createClientMoneyTransferURL =
      this.createClientMoneyTransferURL.bind(this);
    this.createClientMoneyTransfer = this.createClientMoneyTransfer.bind(this);
    this.deleteClientMoneyBookingURL =
      this.deleteClientMoneyBookingURL.bind(this);
    this.deleteClientMoneyBooking = this.deleteClientMoneyBooking.bind(this);
    this.getClientMoneyBookingURL = this.getClientMoneyBookingURL.bind(this);
    this.getClientMoneyBooking = this.getClientMoneyBooking.bind(this);
    this.isDeleteClientMoneyBookingPossibleURL =
      this.isDeleteClientMoneyBookingPossibleURL.bind(this);
    this.isDeleteClientMoneyBookingPossible =
      this.isDeleteClientMoneyBookingPossible.bind(this);
    this.getClientMoneyDebtorsURL = this.getClientMoneyDebtorsURL.bind(this);
    this.getClientMoneyDebtors = this.getClientMoneyDebtors.bind(this);
    this.getClientMoneyDebtorCasusOverviewURL =
      this.getClientMoneyDebtorCasusOverviewURL.bind(this);
    this.getClientMoneyDebtorCasusOverview =
      this.getClientMoneyDebtorCasusOverview.bind(this);
    this.getAllContactsURL = this.getAllContactsURL.bind(this);
    this.getAllContacts = this.getAllContacts.bind(this);
    this.downloadReportURL = this.downloadReportURL.bind(this);
    this.downloadReport = this.downloadReport.bind(this);
    this.downloadExportAsPdfURL = this.downloadExportAsPdfURL.bind(this);
    this.downloadExportAsPdf = this.downloadExportAsPdf.bind(this);
    this.getInvoiceBookingsURL = this.getInvoiceBookingsURL.bind(this);
    this.getInvoiceBookings = this.getInvoiceBookings.bind(this);
    this.deleteInvoiceContainersURL =
      this.deleteInvoiceContainersURL.bind(this);
    this.deleteInvoiceContainers = this.deleteInvoiceContainers.bind(this);
    this.getInvoiceContainersURL = this.getInvoiceContainersURL.bind(this);
    this.getInvoiceContainers = this.getInvoiceContainers.bind(this);
    this.createInvoiceContainersURL =
      this.createInvoiceContainersURL.bind(this);
    this.createInvoiceContainers = this.createInvoiceContainers.bind(this);
    this.getInvoiceDocumentsURL = this.getInvoiceDocumentsURL.bind(this);
    this.getInvoiceDocuments = this.getInvoiceDocuments.bind(this);
    this.checkIfStatesUpdatableURL = this.checkIfStatesUpdatableURL.bind(this);
    this.checkIfStatesUpdatable = this.checkIfStatesUpdatable.bind(this);
    this.setStatesURL = this.setStatesURL.bind(this);
    this.setStates = this.setStates.bind(this);
    this.getInvoiceContainerURL = this.getInvoiceContainerURL.bind(this);
    this.getInvoiceContainer = this.getInvoiceContainer.bind(this);
    this.addFeeCompletersURL = this.addFeeCompletersURL.bind(this);
    this.addFeeCompleters = this.addFeeCompleters.bind(this);
    this.addFeesAsItemsURL = this.addFeesAsItemsURL.bind(this);
    this.addFeesAsItems = this.addFeesAsItems.bind(this);
    this.cancelInvoiceContainerURL = this.cancelInvoiceContainerURL.bind(this);
    this.cancelInvoiceContainer = this.cancelInvoiceContainer.bind(this);
    this.getChangeTotalSummaryURL = this.getChangeTotalSummaryURL.bind(this);
    this.getChangeTotalSummary = this.getChangeTotalSummary.bind(this);
    this.changeTotalURL = this.changeTotalURL.bind(this);
    this.changeTotal = this.changeTotal.bind(this);
    this.removeContainerItemFromInvoiceURL =
      this.removeContainerItemFromInvoiceURL.bind(this);
    this.removeContainerItemFromInvoice =
      this.removeContainerItemFromInvoice.bind(this);
    this.updateActivityContainerItemURL =
      this.updateActivityContainerItemURL.bind(this);
    this.updateActivityContainerItem =
      this.updateActivityContainerItem.bind(this);
    this.updateAdvancePaymentContainerItemURL =
      this.updateAdvancePaymentContainerItemURL.bind(this);
    this.updateAdvancePaymentContainerItem =
      this.updateAdvancePaymentContainerItem.bind(this);
    this.addExpenseContainerItemURL =
      this.addExpenseContainerItemURL.bind(this);
    this.addExpenseContainerItem = this.addExpenseContainerItem.bind(this);
    this.updateExpenseContainerItemURL =
      this.updateExpenseContainerItemURL.bind(this);
    this.updateExpenseContainerItem =
      this.updateExpenseContainerItem.bind(this);
    this.addExternalExpenseByAbsoluteContainerItemURL =
      this.addExternalExpenseByAbsoluteContainerItemURL.bind(this);
    this.addExternalExpenseByAbsoluteContainerItem =
      this.addExternalExpenseByAbsoluteContainerItem.bind(this);
    this.updateExternalExpenseByAbsoluteContainerItemURL =
      this.updateExternalExpenseByAbsoluteContainerItemURL.bind(this);
    this.updateExternalExpenseByAbsoluteContainerItem =
      this.updateExternalExpenseByAbsoluteContainerItem.bind(this);
    this.addExternalExpenseByRateContainerItemURL =
      this.addExternalExpenseByRateContainerItemURL.bind(this);
    this.addExternalExpenseByRateContainerItem =
      this.addExternalExpenseByRateContainerItem.bind(this);
    this.updateExternalExpenseByRateContainerItemURL =
      this.updateExternalExpenseByRateContainerItemURL.bind(this);
    this.updateExternalExpenseByRateContainerItem =
      this.updateExternalExpenseByRateContainerItem.bind(this);
    this.addFlatContainerItemURL = this.addFlatContainerItemURL.bind(this);
    this.addFlatContainerItem = this.addFlatContainerItem.bind(this);
    this.updateFlatContainerItemURL =
      this.updateFlatContainerItemURL.bind(this);
    this.updateFlatContainerItem = this.updateFlatContainerItem.bind(this);
    this.addHourlyContainerItemURL = this.addHourlyContainerItemURL.bind(this);
    this.addHourlyContainerItem = this.addHourlyContainerItem.bind(this);
    this.updateHourlyContainerItemURL =
      this.updateHourlyContainerItemURL.bind(this);
    this.updateHourlyContainerItem = this.updateHourlyContainerItem.bind(this);
    this.deleteContainerItemsURL = this.deleteContainerItemsURL.bind(this);
    this.deleteContainerItems = this.deleteContainerItems.bind(this);
    this.setContainerItemsAttributeURL =
      this.setContainerItemsAttributeURL.bind(this);
    this.setContainerItemsAttribute =
      this.setContainerItemsAttribute.bind(this);
    this.updateContainerItemBillabilityURL =
      this.updateContainerItemBillabilityURL.bind(this);
    this.updateContainerItemBillability =
      this.updateContainerItemBillability.bind(this);
    this.hasExportedReceiptsURL = this.hasExportedReceiptsURL.bind(this);
    this.hasExportedReceipts = this.hasExportedReceipts.bind(this);
    this.hasInvoicesInStateDoneURL = this.hasInvoicesInStateDoneURL.bind(this);
    this.hasInvoicesInStateDone = this.hasInvoicesInStateDone.bind(this);
    this.getInvoiceContainerItemsURL =
      this.getInvoiceContainerItemsURL.bind(this);
    this.getInvoiceContainerItems = this.getInvoiceContainerItems.bind(this);
    this.downloadInvoicePreviewPDFURL =
      this.downloadInvoicePreviewPDFURL.bind(this);
    this.downloadInvoicePreviewPDF = this.downloadInvoicePreviewPDF.bind(this);
    this.setInvoiceRecipientFractionsURL =
      this.setInvoiceRecipientFractionsURL.bind(this);
    this.setInvoiceRecipientFractions =
      this.setInvoiceRecipientFractions.bind(this);
    this.addInvoiceRecipientURL = this.addInvoiceRecipientURL.bind(this);
    this.addInvoiceRecipient = this.addInvoiceRecipient.bind(this);
    this.deleteInvoiceRecipientURL = this.deleteInvoiceRecipientURL.bind(this);
    this.deleteInvoiceRecipient = this.deleteInvoiceRecipient.bind(this);
    this.updateInvoiceRecipientURL = this.updateInvoiceRecipientURL.bind(this);
    this.updateInvoiceRecipient = this.updateInvoiceRecipient.bind(this);
    this.getAllInvoicesInContainerURL =
      this.getAllInvoicesInContainerURL.bind(this);
    this.getAllInvoicesInContainer = this.getAllInvoicesInContainer.bind(this);
    this.searchItemOwnersURL = this.searchItemOwnersURL.bind(this);
    this.searchItemOwners = this.searchItemOwners.bind(this);
    this.getInvoiceContainerItemStatisticsURL =
      this.getInvoiceContainerItemStatisticsURL.bind(this);
    this.getInvoiceContainerItemStatistics =
      this.getInvoiceContainerItemStatistics.bind(this);
    this.getPaymentAssignmentCandidatesURL =
      this.getPaymentAssignmentCandidatesURL.bind(this);
    this.getPaymentAssignmentCandidates =
      this.getPaymentAssignmentCandidates.bind(this);
    this.refreshPaymentItemsURL = this.refreshPaymentItemsURL.bind(this);
    this.refreshPaymentItems = this.refreshPaymentItems.bind(this);
    this.replacePaymentItemsURL = this.replacePaymentItemsURL.bind(this);
    this.replacePaymentItems = this.replacePaymentItems.bind(this);
    this.deleteContainerPaymentItemURL =
      this.deleteContainerPaymentItemURL.bind(this);
    this.deleteContainerPaymentItem =
      this.deleteContainerPaymentItem.bind(this);
    this.updateContainerPaymentItemURL =
      this.updateContainerPaymentItemURL.bind(this);
    this.updateContainerPaymentItem =
      this.updateContainerPaymentItem.bind(this);
    this.setCancellationDateURL = this.setCancellationDateURL.bind(this);
    this.setCancellationDate = this.setCancellationDate.bind(this);
    this.setStateURL = this.setStateURL.bind(this);
    this.setState = this.setState.bind(this);
    this.setInvoiceContainerSettingsURL =
      this.setInvoiceContainerSettingsURL.bind(this);
    this.setInvoiceContainerSettings =
      this.setInvoiceContainerSettings.bind(this);
    this.setInvoiceContainerPercentageFeeURL =
      this.setInvoiceContainerPercentageFeeURL.bind(this);
    this.setInvoiceContainerPercentageFee =
      this.setInvoiceContainerPercentageFee.bind(this);
    this.getInvoiceDefaultsURL = this.getInvoiceDefaultsURL.bind(this);
    this.getInvoiceDefaults = this.getInvoiceDefaults.bind(this);
    this.getInvoicesURL = this.getInvoicesURL.bind(this);
    this.getInvoices = this.getInvoices.bind(this);
    this.getInvoiceURL = this.getInvoiceURL.bind(this);
    this.getInvoice = this.getInvoice.bind(this);
    this.createCancellationPdfEmailURL =
      this.createCancellationPdfEmailURL.bind(this);
    this.createCancellationPdfEmail =
      this.createCancellationPdfEmail.bind(this);
    this.createCancellationWordDocumentEmailURL =
      this.createCancellationWordDocumentEmailURL.bind(this);
    this.createCancellationWordDocumentEmail =
      this.createCancellationWordDocumentEmail.bind(this);
    this.createClientSideCancellationPdfEmailURL =
      this.createClientSideCancellationPdfEmailURL.bind(this);
    this.createClientSideCancellationPdfEmail =
      this.createClientSideCancellationPdfEmail.bind(this);
    this.createClientSidePdfEmailURL =
      this.createClientSidePdfEmailURL.bind(this);
    this.createClientSidePdfEmail = this.createClientSidePdfEmail.bind(this);
    this.createClientSideReminderPdfEmailURL =
      this.createClientSideReminderPdfEmailURL.bind(this);
    this.createClientSideReminderPdfEmail =
      this.createClientSideReminderPdfEmail.bind(this);
    this.createTemporaryInvoiceReminderDocumentFromTemplateURL =
      this.createTemporaryInvoiceReminderDocumentFromTemplateURL.bind(this);
    this.createTemporaryInvoiceReminderDocumentFromTemplate =
      this.createTemporaryInvoiceReminderDocumentFromTemplate.bind(this);
    this.deleteWriteOffURL = this.deleteWriteOffURL.bind(this);
    this.deleteWriteOff = this.deleteWriteOff.bind(this);
    this.getInvoiceItemsDetailURL = this.getInvoiceItemsDetailURL.bind(this);
    this.getInvoiceItemsDetail = this.getInvoiceItemsDetail.bind(this);
    this.getInvoiceDocumentURL = this.getInvoiceDocumentURL.bind(this);
    this.getInvoiceDocument = this.getInvoiceDocument.bind(this);
    this.isDeleteWriteOffPossibleURL =
      this.isDeleteWriteOffPossibleURL.bind(this);
    this.isDeleteWriteOffPossible = this.isDeleteWriteOffPossible.bind(this);
    this.isRevokePaymentPossibleURL =
      this.isRevokePaymentPossibleURL.bind(this);
    this.isRevokePaymentPossible = this.isRevokePaymentPossible.bind(this);
    this.createPdfEmailURL = this.createPdfEmailURL.bind(this);
    this.createPdfEmail = this.createPdfEmail.bind(this);
    this.createReminderPdfEmailURL = this.createReminderPdfEmailURL.bind(this);
    this.createReminderPdfEmail = this.createReminderPdfEmail.bind(this);
    this.createReminderWordDocumentEmailURL =
      this.createReminderWordDocumentEmailURL.bind(this);
    this.createReminderWordDocumentEmail =
      this.createReminderWordDocumentEmail.bind(this);
    this.getInvoiceRemindersURL = this.getInvoiceRemindersURL.bind(this);
    this.getInvoiceReminders = this.getInvoiceReminders.bind(this);
    this.createInvoiceReminderURL = this.createInvoiceReminderURL.bind(this);
    this.createInvoiceReminder = this.createInvoiceReminder.bind(this);
    this.revokePaymentURL = this.revokePaymentURL.bind(this);
    this.revokePayment = this.revokePayment.bind(this);
    this.roundUnderpaymentURL = this.roundUnderpaymentURL.bind(this);
    this.roundUnderpayment = this.roundUnderpayment.bind(this);
    this.createWordDocumentEmailURL =
      this.createWordDocumentEmailURL.bind(this);
    this.createWordDocumentEmail = this.createWordDocumentEmail.bind(this);
    this.writeOffInvoiceURL = this.writeOffInvoiceURL.bind(this);
    this.writeOffInvoice = this.writeOffInvoice.bind(this);
    this.getInvoicingMasterDataURL = this.getInvoicingMasterDataURL.bind(this);
    this.getInvoicingMasterData = this.getInvoicingMasterData.bind(this);
    this.getPaymentBookingsURL = this.getPaymentBookingsURL.bind(this);
    this.getPaymentBookings = this.getPaymentBookings.bind(this);
    this.getPaymentsURL = this.getPaymentsURL.bind(this);
    this.getPayments = this.getPayments.bind(this);
    this.createPaymentURL = this.createPaymentURL.bind(this);
    this.createPayment = this.createPayment.bind(this);
    this.deletePaymentURL = this.deletePaymentURL.bind(this);
    this.deletePayment = this.deletePayment.bind(this);
    this.getPaymentDetailURL = this.getPaymentDetailURL.bind(this);
    this.getPaymentDetail = this.getPaymentDetail.bind(this);
    this.getCasusIdForPaymentURL = this.getCasusIdForPaymentURL.bind(this);
    this.getCasusIdForPayment = this.getCasusIdForPayment.bind(this);
    this.isDeletePaymentPossibleURL =
      this.isDeletePaymentPossibleURL.bind(this);
    this.isDeletePaymentPossible = this.isDeletePaymentPossible.bind(this);
    this.isRoundOverpaymentPossibleURL =
      this.isRoundOverpaymentPossibleURL.bind(this);
    this.isRoundOverpaymentPossible =
      this.isRoundOverpaymentPossible.bind(this);
    this.deletePaymentBookingGroupURL =
      this.deletePaymentBookingGroupURL.bind(this);
    this.deletePaymentBookingGroup = this.deletePaymentBookingGroup.bind(this);
    this.isDeletePaymentBookingGroupPossibleURL =
      this.isDeletePaymentBookingGroupPossibleURL.bind(this);
    this.isDeletePaymentBookingGroupPossible =
      this.isDeletePaymentBookingGroupPossible.bind(this);
    this.repayPaymentURL = this.repayPaymentURL.bind(this);
    this.repayPayment = this.repayPayment.bind(this);
    this.roundOverpaymentURL = this.roundOverpaymentURL.bind(this);
    this.roundOverpayment = this.roundOverpayment.bind(this);
    this.transferPaymentURL = this.transferPaymentURL.bind(this);
    this.transferPayment = this.transferPayment.bind(this);
    this.exportReceiptAsPdfURL = this.exportReceiptAsPdfURL.bind(this);
    this.exportReceiptAsPdf = this.exportReceiptAsPdf.bind(this);
    this.getInvoiceReminderDefaultsURL =
      this.getInvoiceReminderDefaultsURL.bind(this);
    this.getInvoiceReminderDefaults =
      this.getInvoiceReminderDefaults.bind(this);
    this.getTemplatesURL = this.getTemplatesURL.bind(this);
    this.getTemplates = this.getTemplates.bind(this);
    this.getOverridableTemplatePlaceholdersURL =
      this.getOverridableTemplatePlaceholdersURL.bind(this);
    this.getOverridableTemplatePlaceholders =
      this.getOverridableTemplatePlaceholders.bind(this);
    this.getReportByDayURL = this.getReportByDayURL.bind(this);
    this.getReportByDay = this.getReportByDay.bind(this);
    this.deleteFeeURL = this.deleteFeeURL.bind(this);
    this.deleteFee = this.deleteFee.bind(this);
    this.getReportByPeriodURL = this.getReportByPeriodURL.bind(this);
    this.getReportByPeriod = this.getReportByPeriod.bind(this);
    this.getTransactionAssignmentCandidatesURL =
      this.getTransactionAssignmentCandidatesURL.bind(this);
    this.getTransactionAssignmentCandidates =
      this.getTransactionAssignmentCandidates.bind(this);
    this.getTransactionSetsURL = this.getTransactionSetsURL.bind(this);
    this.getTransactionSets = this.getTransactionSets.bind(this);
    this.importTransactionSetURL = this.importTransactionSetURL.bind(this);
    this.importTransactionSet = this.importTransactionSet.bind(this);
    this.deleteTransactionSetURL = this.deleteTransactionSetURL.bind(this);
    this.deleteTransactionSet = this.deleteTransactionSet.bind(this);
    this.assignMatchedTransactionsURL =
      this.assignMatchedTransactionsURL.bind(this);
    this.assignMatchedTransactions = this.assignMatchedTransactions.bind(this);
    this.exportTransactionSetAsPdfURL =
      this.exportTransactionSetAsPdfURL.bind(this);
    this.exportTransactionSetAsPdf = this.exportTransactionSetAsPdf.bind(this);
    this.getTransactionSetTransactionsURL =
      this.getTransactionSetTransactionsURL.bind(this);
    this.getTransactionSetTransactions =
      this.getTransactionSetTransactions.bind(this);
    this.assignMatchedTransactionURL =
      this.assignMatchedTransactionURL.bind(this);
    this.assignMatchedTransaction = this.assignMatchedTransaction.bind(this);
    this.assignTransactionURL = this.assignTransactionURL.bind(this);
    this.assignTransaction = this.assignTransaction.bind(this);
    this.markTransactionAsDuplicateURL =
      this.markTransactionAsDuplicateURL.bind(this);
    this.markTransactionAsDuplicate =
      this.markTransactionAsDuplicate.bind(this);
    this.markTransactionAsIgnoredURL =
      this.markTransactionAsIgnoredURL.bind(this);
    this.markTransactionAsIgnored = this.markTransactionAsIgnored.bind(this);
    this.resetTransactionStateURL = this.resetTransactionStateURL.bind(this);
    this.resetTransactionState = this.resetTransactionState.bind(this);
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  public setRequestHeadersHandler(handler: RequestHeadersHandler): void {
    this.requestHeadersHandler = handler;
  }

  public setConfigureAgentHandler(handler: ConfigureAgentHandler): void {
    this.configureAgentHandler = handler;
  }

  public setConfigureRequestHandler(handler: ConfigureRequestHandler): void {
    this.configureRequestHandler = handler;
  }

  public request(
    method: string,
    url: string,
    body: any,
    headers: RequestHeaders,
    form: any,
    reject: CallbackHandler,
    resolve: CallbackHandler,
    progress: CallbackHandler,
    opts: CommonRequestOptions,
    signal?: AbortSignal,
    skipRedirectOnUnauthorized = false
  ): void {
    const agent = this.configureAgentHandler
      ? this.configureAgentHandler(superAgentRequest)
      : superAgentRequest;

    if (process.env.NODE_ENV === 'test') {
      throw new Error(
        'Calling endpoints in test environment should be avoided, this endpoint needs to be mocked: ' +
          url +
          ' [' +
          method +
          ']'
      );
    }

    let request = agent(method, url);
    if (this.configureRequestHandler) {
      request = this.configureRequestHandler(request);
    }

    let processedHeaders = headers;
    if (this.requestHeadersHandler) {
      processedHeaders = this.requestHeadersHandler({...headers});
    }

    if (body) {
      request.send(body);

      if (typeof body === 'object' && !(body.constructor.name === 'Buffer')) {
        processedHeaders['Content-Type'] = 'application/json';
      }
    }

    if (Object.keys(form).length > 0) {
      // do not explicitly set the the content type
      // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post#39281156
      delete processedHeaders['content-type'];
      const formData = new FormData();

      Object.keys(form).forEach((key: string) =>
        formData.append(key, form[key])
      );
      request.send(formData);
    }

    request.set(processedHeaders);

    if (opts.$retries && opts.$retries > 0) {
      request.retry(opts.$retries);
    }

    if (
      (opts.$timeout && opts.$timeout > 0) ||
      (opts.$deadline && opts.$deadline > 0)
    ) {
      request.timeout({deadline: opts.$deadline, response: opts.$timeout});
    }

    request
      .on('progress', progress)
      .on('abort', () => reject('Aborted'))
      .ok((res) => res.status <= 400)
      .end((error, response) => {
        // an error will also be emitted for a 4xx and 5xx status code
        // the error object will then have error.status and error.response fields
        // see superagent error handling: https://github.com/visionmedia/superagent/blob/master/docs/index.md#error-handling
        if (error) {
          reject(error);
          if (!skipRedirectOnUnauthorized) {
            this.onUnauthorizedHandler?.(error);
          }
          this.errorHandlers.forEach((handler) => handler(error));
        } else {
          resolve(response);
        }
      });

    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  /*
   *
   * @method
   * @name Backend#createAdvancePaymentInvoiceContainer
   * @param {} body -
   */
  public async createAdvancePaymentInvoiceContainer({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AdvancePaymentRequestContainerCreateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateAdvancePaymentRequestContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, CreateAdvancePaymentRequestContainerResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreateAdvancePaymentRequestContainerResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createAdvancePaymentInvoiceContainerURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      CreateAdvancePaymentRequestContainerResponse
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateAdvancePaymentRequestContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateAdvancePaymentRequestContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreateAdvancePaymentRequestContainerResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createAdvancePaymentInvoiceContainerURL(
    parameters: {
      body: AdvancePaymentRequestContainerCreateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/advance-payment-request-containers/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAdvancedPaymentRequestDefaults
   */
  public async getAdvancedPaymentRequestDefaults({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetAdvancedPaymentRequestDefaultsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetAdvancedPaymentRequestDefaultsResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetAdvancedPaymentRequestDefaultsResponse>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAdvancedPaymentRequestDefaultsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      GetAdvancedPaymentRequestDefaultsResponse
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetAdvancedPaymentRequestDefaultsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetAdvancedPaymentRequestDefaultsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetAdvancedPaymentRequestDefaultsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAdvancedPaymentRequestDefaultsURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/advanced-payment-request-defaults/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getBillableCasus
   * @param {string} clientId -
   * @param {string} fromInclusive -
   * @param {string} responsiblePersonUserId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   */
  public async getBillableCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientId?: string;
      fromInclusive?: string;
      responsiblePersonUserId?: string;
      sortFields: Array<
        | 'CASUS_DISPLAY_NAME_WITH_CLIENT'
        | 'RESPONSIBLE_PERSON_INITIALS'
        | 'TOTAL_OPEN_FEES_EXCLUSIVE_TAX'
        | 'TOTAL_BILLABLE_OPEN_FEES_EXCLUSIVE_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetBillableCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetBillableCasusResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetBillableCasusResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getBillableCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetBillableCasusResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetBillableCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetBillableCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetBillableCasusResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getBillableCasusURL(
    parameters: {
      clientId?: string;
      fromInclusive?: string;
      responsiblePersonUserId?: string;
      sortFields: Array<
        | 'CASUS_DISPLAY_NAME_WITH_CLIENT'
        | 'RESPONSIBLE_PERSON_INITIALS'
        | 'TOTAL_OPEN_FEES_EXCLUSIVE_TAX'
        | 'TOTAL_BILLABLE_OPEN_FEES_EXCLUSIVE_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/billable-casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['responsiblePersonUserId'] !== 'undefined') {
      queryParameters['responsiblePersonUserId'] =
        Backend.convertParameterCollectionFormat(
          parameters['responsiblePersonUserId'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusClients
   * @param {array} ids -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async getCasusClients({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetCasusClientsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetCasusClientsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetCasusClientsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getCasusClientsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetCasusClientsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetCasusClientsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetCasusClientsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetCasusClientsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusClientsURL(
    parameters: {
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus-clients/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchCasusDebtors
   * @param {string} casusId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchCasusDebtors({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchCasusDebtorsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchCasusDebtorsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchCasusDebtorsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchCasusDebtorsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchCasusDebtorsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchCasusDebtorsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchCasusDebtorsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchCasusDebtorsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchCasusDebtorsURL(
    parameters: {
      casusId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus-debtors/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getUnpaidInvoices
   * @param {string} casusDebtorId -
   * @param {string} filterType -
   */
  public async getUnpaidInvoices({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusDebtorId: string;
      filterType: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getUnpaidInvoices_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getUnpaidInvoices_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getUnpaidInvoices_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusDebtorId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusDebtorId'));
        return;
      }

      if (typeof parameters['filterType'] === 'undefined') {
        reject(new Error('Missing required  parameter: filterType'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getUnpaidInvoicesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getUnpaidInvoices_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getUnpaidInvoices_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getUnpaidInvoices_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getUnpaidInvoices_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getUnpaidInvoicesURL(
    parameters: {
      casusDebtorId: string;
      filterType: 'INVOICE' | 'ADVANCE_PAYMENT_REQUEST';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus-debtors/{casusDebtorId}/unpaid-invoices/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusDebtorId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusDebtorId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['filterType'] !== 'undefined') {
      queryParameters['filterType'] = Backend.convertParameterCollectionFormat(
        parameters['filterType'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusDebtors
   * @param {string} casusId -
   */
  public async getCasusDebtors({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetCasusDebtorsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetCasusDebtorsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetCasusDebtorsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getCasusDebtorsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetCasusDebtorsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetCasusDebtorsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetCasusDebtorsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetCasusDebtorsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusDebtorsURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/casus-debtors/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDefaultBankAccountId
   * @param {string} casusId -
   */
  public async getDefaultBankAccountId({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: DefaultBankAccountResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, DefaultBankAccountResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, DefaultBankAccountResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDefaultBankAccountIdURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, DefaultBankAccountResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          DefaultBankAccountResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as DefaultBankAccountResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, DefaultBankAccountResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDefaultBankAccountIdURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/default-bank-account/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDefaultInvoiceRecipients
   * @param {string} casusId -
   */
  public async getDefaultInvoiceRecipients({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetCasusDebtorsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetCasusDebtorsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetCasusDebtorsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDefaultInvoiceRecipientsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetCasusDebtorsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetCasusDebtorsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetCasusDebtorsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetCasusDebtorsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDefaultInvoiceRecipientsURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/default-invoice-recipients/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteFees
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async deleteFees({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DeleteFeesInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, FeesResponse>
          | ResponseWithBody<400, FeeInUseCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, FeesResponse>
      | ResponseWithBody<400, FeeInUseCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['sortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: sortFields'));
        return;
      }

      if (typeof parameters['sortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: sortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteFeesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, FeesResponse>
      | ResponseWithBody<400, FeeInUseCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, FeesResponse>
          | ResponseWithBody<400, FeeInUseCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, FeesResponse>
        | ResponseWithBody<400, FeeInUseCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteFeesURL(
    parameters: {
      body: DeleteFeesInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getOpenFees
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async getOpenFees({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getOpenFeesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getOpenFeesURL(
    parameters: {
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExpenseFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async addExpenseFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExpenseFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExpenseFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addExpenseFeeURL(
    parameters: {
      body: ExpenseFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/expense-fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExpenseFee
   * @param {} body -
   * @param {string} casusId -
   * @param {string} expenseFeeId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async updateExpenseFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExpenseFeeInParams;
      casusId: string;
      expenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['expenseFeeId'] === 'undefined') {
          reject(new Error('Missing required  parameter: expenseFeeId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExpenseFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExpenseFeeURL(
    parameters: {
      body: ExpenseFeeInParams;
      casusId: string;
      expenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/expense-fees/{expenseFeeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{expenseFeeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['expenseFeeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExternalExpenseByAbsoluteFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async addExternalExpenseByAbsoluteFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByAbsoluteFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExternalExpenseByAbsoluteFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addExternalExpenseByAbsoluteFeeURL(
    parameters: {
      body: ExternalExpenseByAbsoluteFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/external-expense-by-absolute-fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExternalExpenseByAbsoluteFee
   * @param {} body -
   * @param {string} casusId -
   * @param {string} externalExpenseFeeId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async updateExternalExpenseByAbsoluteFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByAbsoluteFeeInParams;
      casusId: string;
      externalExpenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['externalExpenseFeeId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: externalExpenseFeeId')
          );
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExternalExpenseByAbsoluteFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExternalExpenseByAbsoluteFeeURL(
    parameters: {
      body: ExternalExpenseByAbsoluteFeeInParams;
      casusId: string;
      externalExpenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/external-expense-by-absolute-fees/{externalExpenseFeeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{externalExpenseFeeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['externalExpenseFeeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExternalExpenseByRateFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async addExternalExpenseByRateFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByRateFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExternalExpenseByRateFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addExternalExpenseByRateFeeURL(
    parameters: {
      body: ExternalExpenseByRateFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/external-expense-by-rate-fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExternalExpenseByRateFee
   * @param {} body -
   * @param {string} casusId -
   * @param {string} externalExpenseFeeId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async updateExternalExpenseByRateFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByRateFeeInParams;
      casusId: string;
      externalExpenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['externalExpenseFeeId'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: externalExpenseFeeId')
          );
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExternalExpenseByRateFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExternalExpenseByRateFeeURL(
    parameters: {
      body: ExternalExpenseByRateFeeInParams;
      casusId: string;
      externalExpenseFeeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/external-expense-by-rate-fees/{externalExpenseFeeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{externalExpenseFeeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['externalExpenseFeeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addFlatFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async addFlatFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FlatFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addFlatFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addFlatFeeURL(
    parameters: {
      body: FlatFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/flat-fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateFlatFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} flatFeeId -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async updateFlatFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FlatFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      flatFeeId: string;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['flatFeeId'] === 'undefined') {
          reject(new Error('Missing required  parameter: flatFeeId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateFlatFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateFlatFeeURL(
    parameters: {
      body: FlatFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      flatFeeId: string;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/flat-fees/{flatFeeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    path = path.replace(
      '{flatFeeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['flatFeeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addHourlyFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async addHourlyFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: HourlyFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addHourlyFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addHourlyFeeURL(
    parameters: {
      body: HourlyFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/hourly-fees/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateHourlyFee
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} hourlyFeeId -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async updateHourlyFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: HourlyFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      hourlyFeeId: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['hourlyFeeId'] === 'undefined') {
          reject(new Error('Missing required  parameter: hourlyFeeId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateHourlyFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateHourlyFeeURL(
    parameters: {
      body: HourlyFeeInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      hourlyFeeId: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/casus/{casusId}/fees/hourly-fees/{hourlyFeeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    path = path.replace(
      '{hourlyFeeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['hourlyFeeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#moveFees
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async moveFees({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: MoveFeesInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.moveFeesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public moveFeesURL(
    parameters: {
      body: MoveFeesInParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/move-to-casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addQuickFee
   * @param {} body -
   * @param {string} casusId -
   */
  public async addQuickFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AddQuickFeeParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: AddQuickFeeResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, AddQuickFeeResponse>
          | ResponseWithBody<400, QuickFeeParseCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, AddQuickFeeResponse>
      | ResponseWithBody<400, QuickFeeParseCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.addQuickFeeURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, AddQuickFeeResponse>
      | ResponseWithBody<400, QuickFeeParseCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, AddQuickFeeResponse>
          | ResponseWithBody<400, QuickFeeParseCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as AddQuickFeeResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, AddQuickFeeResponse>
        | ResponseWithBody<400, QuickFeeParseCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public addQuickFeeURL(
    parameters: {
      body: AddQuickFeeParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/quick-fee/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setFeesAttribute
   * @param {} body -
   * @param {string} casusId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async setFeesAttribute({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FeesAttributeUpdateParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setFeesAttributeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setFeesAttributeURL(
    parameters: {
      body: FeesAttributeUpdateParams;
      casusId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/set-attribute/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getFee
   * @param {string} casusId -
   * @param {string} feeId -
   */
  public async getFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      feeId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeeInResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeeInResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeeInResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['feeId'] === 'undefined') {
          reject(new Error('Missing required  parameter: feeId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeeInResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeeInResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeeInResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeeInResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getFeeURL(
    parameters: {
      casusId: string;
      feeId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/{feeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{feeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['feeId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#duplicateFee
   * @param {string} casusId -
   * @param {string} feeId -
   * @param {array} feeProviderUserIds -
   * @param {string} fromInclusive -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} toInclusive -
   * @param {array} types -
   */
  public async duplicateFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      feeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: FeesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, FeesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, FeesResponse>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['feeId'] === 'undefined') {
          reject(new Error('Missing required  parameter: feeId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.duplicateFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, FeesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          FeesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as FeesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, FeesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public duplicateFeeURL(
    parameters: {
      casusId: string;
      feeId: string;
      feeProviderUserIds?: Array<string>;
      fromInclusive?: string;
      query?: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'FEE_PROVIDER_INITIALS'
        | 'SUMMARY'
        | 'DESCRIPTION'
        | 'INTERNAL_NOTE'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      toInclusive?: string;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/fees/{feeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{feeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['feeId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['feeProviderUserIds'] !== 'undefined') {
      queryParameters['feeProviderUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['feeProviderUserIds'],
          ''
        );
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getGroupWithAllPossibleRates
   * @param {string} casusId -
   * @param {string} groupId -
   */
  public async getGroupWithAllPossibleRates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GroupWithAllHourlyRates;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GroupWithAllHourlyRates> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GroupWithAllHourlyRates>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['groupId'] === 'undefined') {
          reject(new Error('Missing required  parameter: groupId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getGroupWithAllPossibleRatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GroupWithAllHourlyRates>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GroupWithAllHourlyRates
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GroupWithAllHourlyRates,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GroupWithAllHourlyRates>,
      header: result.header,
      hasError: true,
    };
  }

  public getGroupWithAllPossibleRatesURL(
    parameters: {
      casusId: string;
      groupId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/group/{groupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{groupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['groupId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getUserWithAllPossibleRates
   * @param {string} casusId -
   * @param {string} userId -
   */
  public async getUserWithAllPossibleRates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      userId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UserWithAllHourlyRates;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UserWithAllHourlyRates> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UserWithAllHourlyRates>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['userId'] === 'undefined') {
          reject(new Error('Missing required  parameter: userId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getUserWithAllPossibleRatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UserWithAllHourlyRates>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UserWithAllHourlyRates
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UserWithAllHourlyRates,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UserWithAllHourlyRates>,
      header: result.header,
      hasError: true,
    };
  }

  public getUserWithAllPossibleRatesURL(
    parameters: {
      casusId: string;
      userId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/user/{userId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchCasusUsers
   * @param {string} casusId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchCasusUsers({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchCasusUsersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchCasusUsersResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchCasusUsersResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchCasusUsersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchCasusUsersResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchCasusUsersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchCasusUsersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchCasusUsersResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchCasusUsersURL(
    parameters: {
      casusId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/casus/{casusId}/users/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getClientMoneyBookings
   * @param {array} bankAccountIds -
   * @param {array} casusIds -
   * @param {array} clientMoneyDebtorIds -
   * @param {string} createdAtFromInclusive -
   * @param {string} createdAtToInclusive -
   * @param {array} createdByUserIds -
   * @param {array} debtorIds -
   * @param {array} ids -
   * @param {array} officeLocationIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} valueDateFromInclusive -
   * @param {string} valueDateToInclusive -
   */
  public async getClientMoneyBookings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      bankAccountIds?: Array<string>;
      casusIds?: Array<string>;
      clientMoneyDebtorIds?: Array<string>;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      debtorIds?: Array<string>;
      ids?: Array<string>;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'VALUE_DATE'
        | 'BOOKING_UNIQUE_NUMBER'
        | 'AMOUNT'
        | 'SUMMARY'
        | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      valueDateFromInclusive?: string;
      valueDateToInclusive?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyBookingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ClientMoneyBookingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ClientMoneyBookingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getClientMoneyBookingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyBookingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyBookingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyBookingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ClientMoneyBookingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getClientMoneyBookingsURL(
    parameters: {
      bankAccountIds?: Array<string>;
      casusIds?: Array<string>;
      clientMoneyDebtorIds?: Array<string>;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      debtorIds?: Array<string>;
      ids?: Array<string>;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'VALUE_DATE'
        | 'BOOKING_UNIQUE_NUMBER'
        | 'AMOUNT'
        | 'SUMMARY'
        | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      valueDateFromInclusive?: string;
      valueDateToInclusive?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/client-money-bookings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['bankAccountIds'] !== 'undefined') {
      queryParameters['bankAccountIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['bankAccountIds'],
          ''
        );
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (typeof parameters['clientMoneyDebtorIds'] !== 'undefined') {
      queryParameters['clientMoneyDebtorIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['clientMoneyDebtorIds'],
          ''
        );
    }

    if (typeof parameters['createdAtFromInclusive'] !== 'undefined') {
      queryParameters['createdAtFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdAtToInclusive'] !== 'undefined') {
      queryParameters['createdAtToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtToInclusive'],
          ''
        );
    }

    if (typeof parameters['createdByUserIds'] !== 'undefined') {
      queryParameters['createdByUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdByUserIds'],
          ''
        );
    }

    if (typeof parameters['debtorIds'] !== 'undefined') {
      queryParameters['debtorIds'] = Backend.convertParameterCollectionFormat(
        parameters['debtorIds'],
        ''
      );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['officeLocationIds'] !== 'undefined') {
      queryParameters['officeLocationIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationIds'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['valueDateFromInclusive'] !== 'undefined') {
      queryParameters['valueDateFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['valueDateFromInclusive'],
          ''
        );
    }

    if (typeof parameters['valueDateToInclusive'] !== 'undefined') {
      queryParameters['valueDateToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['valueDateToInclusive'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createClientMoneyBooking
   * @param {} body -
   */
  public async createClientMoneyBooking({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ClientMoneyBookingCreateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyBookingCreateResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ClientMoneyBookingCreateResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, ClientMoneyBookingCreateResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createClientMoneyBookingURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyBookingCreateResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyBookingCreateResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyBookingCreateResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        ClientMoneyBookingCreateResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createClientMoneyBookingURL(
    parameters: {
      body: ClientMoneyBookingCreateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/client-money-bookings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createClientMoneyTransfer
   * @param {} body -
   */
  public async createClientMoneyTransfer({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ClientMoneyTransferCreateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyTransferCreateResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ClientMoneyTransferCreateResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, ClientMoneyTransferCreateResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createClientMoneyTransferURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyTransferCreateResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyTransferCreateResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyTransferCreateResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        ClientMoneyTransferCreateResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createClientMoneyTransferURL(
    parameters: {
      body: ClientMoneyTransferCreateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/client-money-bookings/transfer/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteClientMoneyBooking
   * @param {string} clientMoneyBookingId -
   */
  public async deleteClientMoneyBooking({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              ClientMoneyBookingDeletionNotPossibleCondition
            >
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, ClientMoneyBookingDeletionNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['clientMoneyBookingId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientMoneyBookingId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteClientMoneyBookingURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, ClientMoneyBookingDeletionNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              ClientMoneyBookingDeletionNotPossibleCondition
            >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, ClientMoneyBookingDeletionNotPossibleCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteClientMoneyBookingURL(
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/client-money-bookings/{clientMoneyBookingId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientMoneyBookingId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientMoneyBookingId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getClientMoneyBooking
   * @param {string} clientMoneyBookingId -
   */
  public async getClientMoneyBooking({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyBookingDetailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ClientMoneyBookingDetailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, ClientMoneyBookingDetailResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['clientMoneyBookingId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientMoneyBookingId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getClientMoneyBookingURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyBookingDetailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyBookingDetailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyBookingDetailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        ClientMoneyBookingDetailResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getClientMoneyBookingURL(
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/client-money-bookings/{clientMoneyBookingId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientMoneyBookingId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientMoneyBookingId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isDeleteClientMoneyBookingPossible
   * @param {string} clientMoneyBookingId -
   */
  public async isDeleteClientMoneyBookingPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsClientMoneyBookingDeletionPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, IsClientMoneyBookingDeletionPossible>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, IsClientMoneyBookingDeletionPossible>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['clientMoneyBookingId'] === 'undefined') {
        reject(new Error('Missing required  parameter: clientMoneyBookingId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.isDeleteClientMoneyBookingPossibleURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsClientMoneyBookingDeletionPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsClientMoneyBookingDeletionPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsClientMoneyBookingDeletionPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        IsClientMoneyBookingDeletionPossible
      >,
      header: result.header,
      hasError: true,
    };
  }

  public isDeleteClientMoneyBookingPossibleURL(
    parameters: {
      clientMoneyBookingId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/client-money-bookings/{clientMoneyBookingId}/delete-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{clientMoneyBookingId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['clientMoneyBookingId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getClientMoneyDebtors
   * @param {boolean} balanceGtZero -
   * @param {array} casusIds -
   * @param {array} debtorIds -
   * @param {array} ids -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getClientMoneyDebtors({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      balanceGtZero?: boolean;
      casusIds?: Array<string>;
      debtorIds?: Array<string>;
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'DEBTOR_DISPLAY_NAME'
        | 'BALANCE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyDebtorsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ClientMoneyDebtorsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ClientMoneyDebtorsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getClientMoneyDebtorsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyDebtorsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyDebtorsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyDebtorsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ClientMoneyDebtorsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getClientMoneyDebtorsURL(
    parameters: {
      balanceGtZero?: boolean;
      casusIds?: Array<string>;
      debtorIds?: Array<string>;
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'DEBTOR_DISPLAY_NAME'
        | 'BALANCE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/client-money-debtors/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['balanceGtZero'] !== 'undefined') {
      queryParameters['balanceGtZero'] =
        Backend.convertParameterCollectionFormat(
          parameters['balanceGtZero'],
          ''
        );
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (typeof parameters['debtorIds'] !== 'undefined') {
      queryParameters['debtorIds'] = Backend.convertParameterCollectionFormat(
        parameters['debtorIds'],
        ''
      );
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getClientMoneyDebtorCasusOverview
   * @param {string} casusId -
   */
  public async getClientMoneyDebtorCasusOverview({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ClientMoneyDebtorCasusOverviewResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, ClientMoneyDebtorCasusOverviewResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, ClientMoneyDebtorCasusOverviewResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getClientMoneyDebtorCasusOverviewURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ClientMoneyDebtorCasusOverviewResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ClientMoneyDebtorCasusOverviewResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ClientMoneyDebtorCasusOverviewResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        ClientMoneyDebtorCasusOverviewResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getClientMoneyDebtorCasusOverviewURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/client-money-debtors/{casusId}/overview/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllContacts
   * @param {array} ids -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async getAllContacts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetAllContactsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetAllContactsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetAllContactsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getAllContactsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetAllContactsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetAllContactsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetAllContactsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetAllContactsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getAllContactsURL(
    parameters: {
      ids?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/contacts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['ids'] !== 'undefined') {
      queryParameters['ids'] = Backend.convertParameterCollectionFormat(
        parameters['ids'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadReport
   * @param {string} fromInclusive -
   * @param {boolean} invoiced -
   * @param {string} toInclusive -
   */
  public async downloadReport({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      fromInclusive?: string;
      invoiced: boolean;
      toInclusive?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadReport_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_downloadReport_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_downloadReport_200>> =
      new Promise((resolve, reject) => {
        headers['accept'] = 'text/csv';

        if (typeof parameters['invoiced'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiced'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.downloadReportURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadReport_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadReport_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadReport_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_downloadReport_200>,
      header: result.header,
      hasError: true,
    };
  }

  public downloadReportURL(
    parameters: {
      fromInclusive?: string;
      invoiced: boolean;
      toInclusive?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/download-report/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['fromInclusive'] !== 'undefined') {
      queryParameters['fromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['fromInclusive'],
          ''
        );
    }

    if (typeof parameters['invoiced'] !== 'undefined') {
      queryParameters['invoiced'] = Backend.convertParameterCollectionFormat(
        parameters['invoiced'],
        ''
      );
    }

    if (typeof parameters['toInclusive'] !== 'undefined') {
      queryParameters['toInclusive'] = Backend.convertParameterCollectionFormat(
        parameters['toInclusive'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadExportAsPdf
   * @param {string} handle -
   */
  public async downloadExportAsPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      handle: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadExportAsPdf_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_downloadExportAsPdf_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadExportAsPdf_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/pdf';

      if (typeof parameters['handle'] === 'undefined') {
        reject(new Error('Missing required  parameter: handle'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadExportAsPdfURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadExportAsPdf_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadExportAsPdf_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadExportAsPdf_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadExportAsPdf_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadExportAsPdfURL(
    parameters: {
      handle: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/downloads/pdf/{handle}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{handle}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['handle'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceBookings
   * @param {string} invoiceId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getInvoiceBookings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
      sortFields: Array<'CREATED_AT' | 'VALUE_DATE'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetBookingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetBookingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetBookingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceBookingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetBookingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetBookingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetBookingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetBookingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceBookingsURL(
    parameters: {
      invoiceId: string;
      sortFields: Array<'CREATED_AT' | 'VALUE_DATE'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-bookings/{invoiceId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteInvoiceContainers
   * @param {} body -
   */
  public async deleteInvoiceContainers({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceContainersDeleteParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteInvoiceContainersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteInvoiceContainersURL(
    parameters: {
      body: InvoiceContainersDeleteParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceContainers
   * @param {array} casusIds -
   * @param {string} clientId -
   * @param {string} createdAtFromInclusive -
   * @param {string} createdAtToInclusive -
   * @param {array} createdByUserIds -
   * @param {string} invoiceDateFromInclusive -
   * @param {string} invoiceDateToInclusive -
   * @param {array} officeLocationIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} recipientAddresseeContactIds -
   * @param {array} responsiblePersonUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} states -
   * @param {array} types -
   */
  public async getInvoiceContainers({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusIds?: Array<string>;
      clientId?: string;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      invoiceDateFromInclusive?: string;
      invoiceDateToInclusive?: string;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      recipientAddresseeContactIds?: Array<string>;
      responsiblePersonUserIds?: Array<string>;
      sortFields: Array<
        | 'STATE'
        | 'INVOICE_CONTAINER_UNIQUE_NUMBER'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'INVOICE_DATE'
        | 'TOTAL_AFTER_TAX'
        | 'OWED_AMOUNT'
        | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<
        'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED'
      >;
      types?: Array<'INVOICE' | 'ADVANCE_PAYMENT_REQUEST'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainersResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainersResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceContainersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainersResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainersResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceContainersURL(
    parameters: {
      casusIds?: Array<string>;
      clientId?: string;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      invoiceDateFromInclusive?: string;
      invoiceDateToInclusive?: string;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      recipientAddresseeContactIds?: Array<string>;
      responsiblePersonUserIds?: Array<string>;
      sortFields: Array<
        | 'STATE'
        | 'INVOICE_CONTAINER_UNIQUE_NUMBER'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'INVOICE_DATE'
        | 'TOTAL_AFTER_TAX'
        | 'OWED_AMOUNT'
        | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<
        'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED'
      >;
      types?: Array<'INVOICE' | 'ADVANCE_PAYMENT_REQUEST'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['createdAtFromInclusive'] !== 'undefined') {
      queryParameters['createdAtFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdAtToInclusive'] !== 'undefined') {
      queryParameters['createdAtToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtToInclusive'],
          ''
        );
    }

    if (typeof parameters['createdByUserIds'] !== 'undefined') {
      queryParameters['createdByUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdByUserIds'],
          ''
        );
    }

    if (typeof parameters['invoiceDateFromInclusive'] !== 'undefined') {
      queryParameters['invoiceDateFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceDateFromInclusive'],
          ''
        );
    }

    if (typeof parameters['invoiceDateToInclusive'] !== 'undefined') {
      queryParameters['invoiceDateToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceDateToInclusive'],
          ''
        );
    }

    if (typeof parameters['officeLocationIds'] !== 'undefined') {
      queryParameters['officeLocationIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationIds'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['recipientAddresseeContactIds'] !== 'undefined') {
      queryParameters['recipientAddresseeContactIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['recipientAddresseeContactIds'],
          ''
        );
    }

    if (typeof parameters['responsiblePersonUserIds'] !== 'undefined') {
      queryParameters['responsiblePersonUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['responsiblePersonUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['states'] !== 'undefined') {
      queryParameters['states'] = Backend.convertParameterCollectionFormat(
        parameters['states'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createInvoiceContainers
   * @param {} body -
   */
  public async createInvoiceContainers({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceContainersCreateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceContainersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreateInvoiceContainersResponse>
          | ResponseWithBody<400, CasusNotInvoiceableCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreateInvoiceContainersResponse>
      | ResponseWithBody<400, CasusNotInvoiceableCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createInvoiceContainersURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreateInvoiceContainersResponse>
      | ResponseWithBody<400, CasusNotInvoiceableCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreateInvoiceContainersResponse>
          | ResponseWithBody<400, CasusNotInvoiceableCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceContainersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreateInvoiceContainersResponse>
        | ResponseWithBody<400, CasusNotInvoiceableCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createInvoiceContainersURL(
    parameters: {
      body: InvoiceContainersCreateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceDocuments
   * @param {array} invoiceContainerIds -
   */
  public async getInvoiceDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceDocumentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerIds'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerIds'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceDocumentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceDocumentsURL(
    parameters: {
      invoiceContainerIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/bulk/invoice-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['invoiceContainerIds'] !== 'undefined') {
      queryParameters['invoiceContainerIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerIds'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkIfStatesUpdatable
   * @param {array} invoiceContainerIds -
   * @param {string} state -
   */
  public async checkIfStatesUpdatable({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerIds: Array<string>;
      state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: StateUpdateCheckResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, StateUpdateCheckResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, StateUpdateCheckResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerIds'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerIds'));
          return;
        }

        if (typeof parameters['state'] === 'undefined') {
          reject(new Error('Missing required  parameter: state'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.checkIfStatesUpdatableURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, StateUpdateCheckResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          StateUpdateCheckResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as StateUpdateCheckResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, StateUpdateCheckResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public checkIfStatesUpdatableURL(
    parameters: {
      invoiceContainerIds: Array<string>;
      state: 'PREPARATION' | 'EDITING' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/bulk/state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['invoiceContainerIds'] !== 'undefined') {
      queryParameters['invoiceContainerIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerIds'],
          ''
        );
    }

    if (typeof parameters['state'] !== 'undefined') {
      queryParameters['state'] = Backend.convertParameterCollectionFormat(
        parameters['state'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setStates
   * @param {} body -
   */
  public async setStates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: BulkStateUpdateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: StateUpdateResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, StateUpdateResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, StateUpdateResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.setStatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, StateUpdateResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          StateUpdateResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as StateUpdateResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, StateUpdateResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setStatesURL(
    parameters: {
      body: BulkStateUpdateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/bulk/state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceContainer
   * @param {string} invoiceContainerId -
   */
  public async getInvoiceContainer({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceContainerURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceContainerURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-containers/{invoiceContainerId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addFeeCompleters
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async addFeeCompleters({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AddFeeCompletersParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addFeeCompletersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public addFeeCompletersURL(
    parameters: {
      body: AddFeeCompletersParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/add-fee-completers/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addFeesAsItems
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addFeesAsItems({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AddFeesAsItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.addFeesAsItemsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addFeesAsItemsURL(
    parameters: {
      body: AddFeesAsItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/add-fees-as-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#cancelInvoiceContainer
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async cancelInvoiceContainer({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CancelInvoiceContainerParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CancelInvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CancelInvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CancelInvoiceContainerResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.cancelInvoiceContainerURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CancelInvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CancelInvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CancelInvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CancelInvoiceContainerResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public cancelInvoiceContainerURL(
    parameters: {
      body: CancelInvoiceContainerParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/cancel/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getChangeTotalSummary
   * @param {boolean} grandTotalIncludeTax -
   * @param {boolean} hourlyFeesIncludeTax -
   * @param {boolean} includeResidual -
   * @param {boolean} includeRoundingDifference -
   * @param {string} invoiceContainerId -
   * @param {string} targetGrandTotalAmount -
   * @param {string} targetHourlyFeesTotalAmount -
   */
  public async getChangeTotalSummary({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      grandTotalIncludeTax?: boolean;
      hourlyFeesIncludeTax?: boolean;
      includeResidual?: boolean;
      includeRoundingDifference?: boolean;
      invoiceContainerId: string;
      targetGrandTotalAmount?: string;
      targetHourlyFeesTotalAmount?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerChangeTotalSummary;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerChangeTotalSummary> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, InvoiceContainerChangeTotalSummary>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getChangeTotalSummaryURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerChangeTotalSummary>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerChangeTotalSummary
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerChangeTotalSummary,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        InvoiceContainerChangeTotalSummary
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getChangeTotalSummaryURL(
    parameters: {
      grandTotalIncludeTax?: boolean;
      hourlyFeesIncludeTax?: boolean;
      includeResidual?: boolean;
      includeRoundingDifference?: boolean;
      invoiceContainerId: string;
      targetGrandTotalAmount?: string;
      targetHourlyFeesTotalAmount?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/change-total-summary/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['grandTotalIncludeTax'] !== 'undefined') {
      queryParameters['grandTotalIncludeTax'] =
        Backend.convertParameterCollectionFormat(
          parameters['grandTotalIncludeTax'],
          ''
        );
    }

    if (typeof parameters['hourlyFeesIncludeTax'] !== 'undefined') {
      queryParameters['hourlyFeesIncludeTax'] =
        Backend.convertParameterCollectionFormat(
          parameters['hourlyFeesIncludeTax'],
          ''
        );
    }

    if (typeof parameters['includeResidual'] !== 'undefined') {
      queryParameters['includeResidual'] =
        Backend.convertParameterCollectionFormat(
          parameters['includeResidual'],
          ''
        );
    }

    if (typeof parameters['includeRoundingDifference'] !== 'undefined') {
      queryParameters['includeRoundingDifference'] =
        Backend.convertParameterCollectionFormat(
          parameters['includeRoundingDifference'],
          ''
        );
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['targetGrandTotalAmount'] !== 'undefined') {
      queryParameters['targetGrandTotal.amount'] =
        Backend.convertParameterCollectionFormat(
          parameters['targetGrandTotalAmount'],
          ''
        );
    }

    if (typeof parameters['targetHourlyFeesTotalAmount'] !== 'undefined') {
      queryParameters['targetHourlyFeesTotal.amount'] =
        Backend.convertParameterCollectionFormat(
          parameters['targetHourlyFeesTotalAmount'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#changeTotal
   * @param {boolean} grandTotalIncludeTax -
   * @param {boolean} hourlyFeesIncludeTax -
   * @param {boolean} includeResidual -
   * @param {boolean} includeRoundingDifference -
   * @param {string} invoiceContainerId -
   * @param {string} targetGrandTotalAmount -
   * @param {string} targetHourlyFeesTotalAmount -
   */
  public async changeTotal({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      grandTotalIncludeTax?: boolean;
      hourlyFeesIncludeTax?: boolean;
      includeResidual?: boolean;
      includeRoundingDifference?: boolean;
      invoiceContainerId: string;
      targetGrandTotalAmount?: string;
      targetHourlyFeesTotalAmount?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.changeTotalURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public changeTotalURL(
    parameters: {
      grandTotalIncludeTax?: boolean;
      hourlyFeesIncludeTax?: boolean;
      includeResidual?: boolean;
      includeRoundingDifference?: boolean;
      invoiceContainerId: string;
      targetGrandTotalAmount?: string;
      targetHourlyFeesTotalAmount?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/change-total-summary/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['grandTotalIncludeTax'] !== 'undefined') {
      queryParameters['grandTotalIncludeTax'] =
        Backend.convertParameterCollectionFormat(
          parameters['grandTotalIncludeTax'],
          ''
        );
    }

    if (typeof parameters['hourlyFeesIncludeTax'] !== 'undefined') {
      queryParameters['hourlyFeesIncludeTax'] =
        Backend.convertParameterCollectionFormat(
          parameters['hourlyFeesIncludeTax'],
          ''
        );
    }

    if (typeof parameters['includeResidual'] !== 'undefined') {
      queryParameters['includeResidual'] =
        Backend.convertParameterCollectionFormat(
          parameters['includeResidual'],
          ''
        );
    }

    if (typeof parameters['includeRoundingDifference'] !== 'undefined') {
      queryParameters['includeRoundingDifference'] =
        Backend.convertParameterCollectionFormat(
          parameters['includeRoundingDifference'],
          ''
        );
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['targetGrandTotalAmount'] !== 'undefined') {
      queryParameters['targetGrandTotal.amount'] =
        Backend.convertParameterCollectionFormat(
          parameters['targetGrandTotalAmount'],
          ''
        );
    }

    if (typeof parameters['targetHourlyFeesTotalAmount'] !== 'undefined') {
      queryParameters['targetHourlyFeesTotal.amount'] =
        Backend.convertParameterCollectionFormat(
          parameters['targetHourlyFeesTotalAmount'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#removeContainerItemFromInvoice
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async removeContainerItemFromInvoice({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DeleteContainerItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.removeContainerItemFromInvoiceURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public removeContainerItemFromInvoiceURL(
    parameters: {
      body: DeleteContainerItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateActivityContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateActivityContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: TimebasedContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateActivityContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateActivityContainerItemURL(
    parameters: {
      body: TimebasedContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/activity-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateAdvancePaymentContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateAdvancePaymentContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AdvancePaymentContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateAdvancePaymentContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateAdvancePaymentContainerItemURL(
    parameters: {
      body: AdvancePaymentContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/advance-payment-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExpenseContainerItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addExpenseContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExpenseContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExpenseContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addExpenseContainerItemURL(
    parameters: {
      body: ExpenseContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/expense-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExpenseContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateExpenseContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExpenseContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExpenseContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExpenseContainerItemURL(
    parameters: {
      body: ExpenseContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/expense-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExternalExpenseByAbsoluteContainerItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addExternalExpenseByAbsoluteContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByAbsoluteContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExternalExpenseByAbsoluteContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addExternalExpenseByAbsoluteContainerItemURL(
    parameters: {
      body: ExternalExpenseByAbsoluteContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/external-expense-by-absolute-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExternalExpenseByAbsoluteContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateExternalExpenseByAbsoluteContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByAbsoluteContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExternalExpenseByAbsoluteContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExternalExpenseByAbsoluteContainerItemURL(
    parameters: {
      body: ExternalExpenseByAbsoluteContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/external-expense-by-absolute-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addExternalExpenseByRateContainerItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addExternalExpenseByRateContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByRateContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addExternalExpenseByRateContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addExternalExpenseByRateContainerItemURL(
    parameters: {
      body: ExternalExpenseByRateContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/external-expense-by-rate-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateExternalExpenseByRateContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateExternalExpenseByRateContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ExternalExpenseByRateContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateExternalExpenseByRateContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateExternalExpenseByRateContainerItemURL(
    parameters: {
      body: ExternalExpenseByRateContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/external-expense-by-rate-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addFlatContainerItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addFlatContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FlatContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addFlatContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addFlatContainerItemURL(
    parameters: {
      body: FlatContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/flat-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateFlatContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateFlatContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FlatContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateFlatContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateFlatContainerItemURL(
    parameters: {
      body: FlatContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/flat-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addHourlyContainerItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async addHourlyContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: TimebasedContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addHourlyContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public addHourlyContainerItemURL(
    parameters: {
      body: TimebasedContainerItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/hourly-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateHourlyContainerItem
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateHourlyContainerItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: TimebasedContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateHourlyContainerItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateHourlyContainerItemURL(
    parameters: {
      body: TimebasedContainerItemInParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/hourly-items/{containerItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteContainerItems
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async deleteContainerItems({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DeleteContainerItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteContainerItemsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteContainerItemsURL(
    parameters: {
      body: DeleteContainerItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/manual-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setContainerItemsAttribute
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async setContainerItemsAttribute({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ContainerItemsAttributeUpdateParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setContainerItemsAttributeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public setContainerItemsAttributeURL(
    parameters: {
      body: ContainerItemsAttributeUpdateParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/set-attribute/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateContainerItemBillability
   * @param {} body -
   * @param {string} containerItemId -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateContainerItemBillability({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ContainerItemBillabilityUpdateParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['containerItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: containerItemId'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateContainerItemBillabilityURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateContainerItemBillabilityURL(
    parameters: {
      body: ContainerItemBillabilityUpdateParams;
      containerItemId: string;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/container-items/{containerItemId}/billability/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{containerItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['containerItemId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#hasExportedReceipts
   * @param {string} invoiceContainerId -
   */
  public async hasExportedReceipts({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: HasExportedReceiptsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, HasExportedReceiptsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, HasExportedReceiptsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.hasExportedReceiptsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, HasExportedReceiptsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          HasExportedReceiptsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as HasExportedReceiptsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, HasExportedReceiptsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public hasExportedReceiptsURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/has-exported-receipts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#hasInvoicesInStateDone
   * @param {string} invoiceContainerId -
   */
  public async hasInvoicesInStateDone({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: HasInvoicesInStateDoneResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, HasInvoicesInStateDoneResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, HasInvoicesInStateDoneResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.hasInvoicesInStateDoneURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, HasInvoicesInStateDoneResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          HasInvoicesInStateDoneResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as HasInvoicesInStateDoneResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        HasInvoicesInStateDoneResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public hasInvoicesInStateDoneURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/has-invoices-in-state-done/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceContainerItems
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async getInvoiceContainerItems({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceContainerItemsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceContainerItemsURL(
    parameters: {
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-container-items/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadInvoicePreviewPDF
   * @param {string} invoiceContainerId -
   */
  public async downloadInvoicePreviewPDF({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadInvoicePreviewPDF_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadInvoicePreviewPDF_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadInvoicePreviewPDF_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/pdf';

      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadInvoicePreviewPDFURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadInvoicePreviewPDF_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadInvoicePreviewPDF_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadInvoicePreviewPDF_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadInvoicePreviewPDF_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadInvoicePreviewPDFURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-preview/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setInvoiceRecipientFractions
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async setInvoiceRecipientFractions({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceRecipientFractionsUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setInvoiceRecipientFractionsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setInvoiceRecipientFractionsURL(
    parameters: {
      body: InvoiceRecipientFractionsUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-recipient-fractions/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#addInvoiceRecipient
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async addInvoiceRecipient({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceRecipientStoringParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.addInvoiceRecipientURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public addInvoiceRecipientURL(
    parameters: {
      body: InvoiceRecipientStoringParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-recipients/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteInvoiceRecipient
   * @param {string} invoiceContainerId -
   * @param {string} invoiceRecipientId -
   */
  public async deleteInvoiceRecipient({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
      invoiceRecipientId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['invoiceRecipientId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceRecipientId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteInvoiceRecipientURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteInvoiceRecipientURL(
    parameters: {
      invoiceContainerId: string;
      invoiceRecipientId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-recipients/{invoiceRecipientId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceRecipientId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceRecipientId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateInvoiceRecipient
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {string} invoiceRecipientId -
   */
  public async updateInvoiceRecipient({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceRecipientStoringParams;
      invoiceContainerId: string;
      invoiceRecipientId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['invoiceRecipientId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceRecipientId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateInvoiceRecipientURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public updateInvoiceRecipientURL(
    parameters: {
      body: InvoiceRecipientStoringParams;
      invoiceContainerId: string;
      invoiceRecipientId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoice-recipients/{invoiceRecipientId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceRecipientId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceRecipientId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllInvoicesInContainer
   * @param {string} invoiceContainerId -
   */
  public async getAllInvoicesInContainer({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoicesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoicesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoicesResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getAllInvoicesInContainerURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoicesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoicesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoicesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoicesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getAllInvoicesInContainerURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/invoices/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchItemOwners
   * @param {string} invoiceContainerId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchItemOwners({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchItemOwnersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchItemOwnersResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SearchItemOwnersResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.searchItemOwnersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchItemOwnersResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchItemOwnersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchItemOwnersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchItemOwnersResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchItemOwnersURL(
    parameters: {
      invoiceContainerId: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/item-owners/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceContainerItemStatistics
   * @param {string} invoiceContainerId -
   */
  public async getInvoiceContainerItemStatistics({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerItemStatisticsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, InvoiceContainerItemStatisticsResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, InvoiceContainerItemStatisticsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getInvoiceContainerItemStatisticsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerItemStatisticsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerItemStatisticsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerItemStatisticsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        InvoiceContainerItemStatisticsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceContainerItemStatisticsURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/item-statistics/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPaymentAssignmentCandidates
   * @param {string} invoiceContainerId -
   */
  public async getPaymentAssignmentCandidates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetPaymentAssignmentCandidatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetPaymentAssignmentCandidatesResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetPaymentAssignmentCandidatesResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['invoiceContainerId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceContainerId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getPaymentAssignmentCandidatesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetPaymentAssignmentCandidatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetPaymentAssignmentCandidatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetPaymentAssignmentCandidatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetPaymentAssignmentCandidatesResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getPaymentAssignmentCandidatesURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/payment-assignment-candidates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#refreshPaymentItems
   * @param {string} invoiceContainerId -
   */
  public async refreshPaymentItems({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.refreshPaymentItemsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public refreshPaymentItemsURL(
    parameters: {
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/payment-items/refresh/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#replacePaymentItems
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async replacePaymentItems({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ReplacePaymentItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.replacePaymentItemsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public replacePaymentItemsURL(
    parameters: {
      body: ReplacePaymentItemsParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/payment-items/replace/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteContainerPaymentItem
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {string} paymentItemId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async deleteContainerPaymentItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      paymentItemId: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['paymentItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentItemId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteContainerPaymentItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteContainerPaymentItemURL(
    parameters: {
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      paymentItemId: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/payment-items/{paymentItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    path = path.replace(
      '{paymentItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentItemId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#updateContainerPaymentItem
   * @param {} body -
   * @param {string} invoiceContainerId -
   * @param {array} itemOwnerUserIds -
   * @param {string} paymentItemId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} types -
   */
  public async updateContainerPaymentItem({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ContainerPaymentItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      paymentItemId: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ContainerItemsWithTotals;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, ContainerItemsWithTotals> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, ContainerItemsWithTotals>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        if (typeof parameters['paymentItemId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentItemId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.updateContainerPaymentItemURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, ContainerItemsWithTotals>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          ContainerItemsWithTotals
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ContainerItemsWithTotals,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, ContainerItemsWithTotals>,
      header: result.header,
      hasError: true,
    };
  }

  public updateContainerPaymentItemURL(
    parameters: {
      body: ContainerPaymentItemInParams;
      invoiceContainerId: string;
      itemOwnerUserIds?: Array<string>;
      paymentItemId: string;
      sortFields: Array<
        | 'INCURRED_AT'
        | 'ITEM_OWNER_INITIALS'
        | 'SUMMARY'
        | 'TOTAL_BEFORE_TAX'
        | 'TOTAL_AFTER_TAX'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      types?: Array<
        | 'EXTERNAL_EXPENSE_BY_ABSOLUTE'
        | 'EXTERNAL_EXPENSE_BY_RATE'
        | 'EXPENSE'
        | 'ACTIVITY'
        | 'HOURLY'
        | 'FLAT'
        | 'ADVANCE_PAYMENT'
      >;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/payment-items/{paymentItemId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemOwnerUserIds'] !== 'undefined') {
      queryParameters['itemOwnerUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['itemOwnerUserIds'],
          ''
        );
    }

    path = path.replace(
      '{paymentItemId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentItemId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setCancellationDate
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async setCancellationDate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UpdateCancellationDateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setCancellationDateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setCancellationDateURL(
    parameters: {
      body: UpdateCancellationDateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/set-cancellation-date/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setState
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async setState({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: StateUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setStateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setStateURL(
    parameters: {
      body: StateUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/set-state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setInvoiceContainerSettings
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async setInvoiceContainerSettings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceContainerSettingsUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceContainerResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceContainerResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceContainerResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setInvoiceContainerSettingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceContainerResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceContainerResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceContainerResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceContainerResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setInvoiceContainerSettingsURL(
    parameters: {
      body: InvoiceContainerSettingsUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/settings/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setInvoiceContainerPercentageFee
   * @param {} body -
   * @param {string} invoiceContainerId -
   */
  public async setInvoiceContainerPercentageFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceContainerPercentageFeeUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceContainerId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceContainerId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setInvoiceContainerPercentageFeeURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setInvoiceContainerPercentageFeeURL(
    parameters: {
      body: InvoiceContainerPercentageFeeUpdateParams;
      invoiceContainerId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoice-containers/{invoiceContainerId}/settings/percentage-fee/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceContainerId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceContainerId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceDefaults
   * @param {array} casusIds -
   */
  public async getInvoiceDefaults({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetInvoiceDefaultsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetInvoiceDefaultsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetInvoiceDefaultsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusIds'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusIds'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceDefaultsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetInvoiceDefaultsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetInvoiceDefaultsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetInvoiceDefaultsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetInvoiceDefaultsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceDefaultsURL(
    parameters: {
      casusIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoice-defaults/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoices
   * @param {array} cancellationCreatedByUserIds -
   * @param {string} cancellationDateFromInclusive -
   * @param {string} cancellationDateToInclusive -
   * @param {array} casusIds -
   * @param {string} clientId -
   * @param {string} createdAtFromInclusive -
   * @param {string} createdAtToInclusive -
   * @param {array} createdByUserIds -
   * @param {string} invoiceDateFromInclusive -
   * @param {string} invoiceDateToInclusive -
   * @param {array} officeLocationIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} recipientAddresseeContactIds -
   * @param {array} remindedStates -
   * @param {array} responsiblePersonUserIds -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} states -
   * @param {array} types -
   */
  public async getInvoices({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      cancellationCreatedByUserIds?: Array<string>;
      cancellationDateFromInclusive?: string;
      cancellationDateToInclusive?: string;
      casusIds?: Array<string>;
      clientId?: string;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      invoiceDateFromInclusive?: string;
      invoiceDateToInclusive?: string;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      recipientAddresseeContactIds?: Array<string>;
      remindedStates?: Array<'NOT_REMINDED' | 'REMINDED' | 'OVERDUE'>;
      responsiblePersonUserIds?: Array<string>;
      sortFields: Array<
        | 'STATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'INVOICE_RECIPIENT_NAME'
        | 'INVOICE_DATE'
        | 'INVOICE_DUE_DATE'
        | 'REMINDER_DATE'
        | 'REMINDER_DUE_DATE'
        | 'TOTAL_AFTER_TAX'
        | 'OWED_AMOUNT'
        | 'REMAINING_AMOUNT'
        | 'CREATED_AT'
        | 'CANCELLATION_CREATED_AT'
        | 'CANCELLATION_UNIQUE_NUMBER'
        | 'CANCELLATION_DATE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<'SENT' | 'OVERDUE' | 'DONE' | 'CANCELLED'>;
      types?: Array<'INVOICE' | 'ADVANCE_PAYMENT_REQUEST'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PagedInvoicesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PagedInvoicesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PagedInvoicesResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoicesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PagedInvoicesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PagedInvoicesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PagedInvoicesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PagedInvoicesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoicesURL(
    parameters: {
      cancellationCreatedByUserIds?: Array<string>;
      cancellationDateFromInclusive?: string;
      cancellationDateToInclusive?: string;
      casusIds?: Array<string>;
      clientId?: string;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      invoiceDateFromInclusive?: string;
      invoiceDateToInclusive?: string;
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      recipientAddresseeContactIds?: Array<string>;
      remindedStates?: Array<'NOT_REMINDED' | 'REMINDED' | 'OVERDUE'>;
      responsiblePersonUserIds?: Array<string>;
      sortFields: Array<
        | 'STATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'INVOICE_RECIPIENT_NAME'
        | 'INVOICE_DATE'
        | 'INVOICE_DUE_DATE'
        | 'REMINDER_DATE'
        | 'REMINDER_DUE_DATE'
        | 'TOTAL_AFTER_TAX'
        | 'OWED_AMOUNT'
        | 'REMAINING_AMOUNT'
        | 'CREATED_AT'
        | 'CANCELLATION_CREATED_AT'
        | 'CANCELLATION_UNIQUE_NUMBER'
        | 'CANCELLATION_DATE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<'SENT' | 'OVERDUE' | 'DONE' | 'CANCELLED'>;
      types?: Array<'INVOICE' | 'ADVANCE_PAYMENT_REQUEST'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['cancellationCreatedByUserIds'] !== 'undefined') {
      queryParameters['cancellationCreatedByUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['cancellationCreatedByUserIds'],
          ''
        );
    }

    if (typeof parameters['cancellationDateFromInclusive'] !== 'undefined') {
      queryParameters['cancellationDateFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['cancellationDateFromInclusive'],
          ''
        );
    }

    if (typeof parameters['cancellationDateToInclusive'] !== 'undefined') {
      queryParameters['cancellationDateToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['cancellationDateToInclusive'],
          ''
        );
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['createdAtFromInclusive'] !== 'undefined') {
      queryParameters['createdAtFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdAtToInclusive'] !== 'undefined') {
      queryParameters['createdAtToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtToInclusive'],
          ''
        );
    }

    if (typeof parameters['createdByUserIds'] !== 'undefined') {
      queryParameters['createdByUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdByUserIds'],
          ''
        );
    }

    if (typeof parameters['invoiceDateFromInclusive'] !== 'undefined') {
      queryParameters['invoiceDateFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceDateFromInclusive'],
          ''
        );
    }

    if (typeof parameters['invoiceDateToInclusive'] !== 'undefined') {
      queryParameters['invoiceDateToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceDateToInclusive'],
          ''
        );
    }

    if (typeof parameters['officeLocationIds'] !== 'undefined') {
      queryParameters['officeLocationIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationIds'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['recipientAddresseeContactIds'] !== 'undefined') {
      queryParameters['recipientAddresseeContactIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['recipientAddresseeContactIds'],
          ''
        );
    }

    if (typeof parameters['remindedStates'] !== 'undefined') {
      queryParameters['remindedStates'] =
        Backend.convertParameterCollectionFormat(
          parameters['remindedStates'],
          ''
        );
    }

    if (typeof parameters['responsiblePersonUserIds'] !== 'undefined') {
      queryParameters['responsiblePersonUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['responsiblePersonUserIds'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['states'] !== 'undefined') {
      queryParameters['states'] = Backend.convertParameterCollectionFormat(
        parameters['states'],
        ''
      );
    }

    if (typeof parameters['types'] !== 'undefined') {
      queryParameters['types'] = Backend.convertParameterCollectionFormat(
        parameters['types'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoice
   * @param {string} invoiceId -
   */
  public async getInvoice({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCancellationPdfEmail
   * @param {boolean} createIfMissing -
   * @param {string} invoiceId -
   */
  public async createCancellationPdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['createIfMissing'] === 'undefined') {
          reject(new Error('Missing required  parameter: createIfMissing'));
          return;
        }

        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createCancellationPdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createCancellationPdfEmailURL(
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/cancellation-pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['createIfMissing'] !== 'undefined') {
      queryParameters['createIfMissing'] =
        Backend.convertParameterCollectionFormat(
          parameters['createIfMissing'],
          ''
        );
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createCancellationWordDocumentEmail
   * @param {string} invoiceId -
   */
  public async createCancellationWordDocumentEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createCancellationWordDocumentEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createCancellationWordDocumentEmailURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/cancellation-word-document-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createClientSideCancellationPdfEmail
   * @param {string} invoiceId -
   * @param {string} temporaryDocumentId -
   */
  public async createClientSideCancellationPdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createClientSideCancellationPdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createClientSideCancellationPdfEmailURL(
    parameters: {
      invoiceId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/client-side-cancellation-pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['temporaryDocumentId'] !== 'undefined') {
      queryParameters['temporaryDocumentId'] =
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createClientSidePdfEmail
   * @param {string} invoiceId -
   * @param {string} temporaryDocumentId -
   */
  public async createClientSidePdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createClientSidePdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createClientSidePdfEmailURL(
    parameters: {
      invoiceId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/client-side-pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['temporaryDocumentId'] !== 'undefined') {
      queryParameters['temporaryDocumentId'] =
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createClientSideReminderPdfEmail
   * @param {string} invoiceId -
   * @param {string} reminderId -
   * @param {string} temporaryDocumentId -
   */
  public async createClientSideReminderPdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
      reminderId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['reminderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: reminderId'));
          return;
        }

        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createClientSideReminderPdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createClientSideReminderPdfEmailURL(
    parameters: {
      invoiceId: string;
      reminderId: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/client-side-reminder-pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['reminderId'] !== 'undefined') {
      queryParameters['reminderId'] = Backend.convertParameterCollectionFormat(
        parameters['reminderId'],
        ''
      );
    }

    if (typeof parameters['temporaryDocumentId'] !== 'undefined') {
      queryParameters['temporaryDocumentId'] =
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createTemporaryInvoiceReminderDocumentFromTemplate
   * @param {} body -
   * @param {string} invoiceId -
   */
  public async createTemporaryInvoiceReminderDocumentFromTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateTemporaryInvoiceReminderDocumentFromTemplateParams;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateTemporaryDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateTemporaryDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreateTemporaryDocumentResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['invoiceId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createTemporaryInvoiceReminderDocumentFromTemplateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateTemporaryDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateTemporaryDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateTemporaryDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreateTemporaryDocumentResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createTemporaryInvoiceReminderDocumentFromTemplateURL(
    parameters: {
      body: CreateTemporaryInvoiceReminderDocumentFromTemplateParams;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/create-temporary-invoice-reminder-document/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteWriteOff
   * @param {string} invoiceId -
   */
  public async deleteWriteOff({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, WriteOffDeletionNotPossibleCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, WriteOffDeletionNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['invoiceId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteWriteOffURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, WriteOffDeletionNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, WriteOffDeletionNotPossibleCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, WriteOffDeletionNotPossibleCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteWriteOffURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/delete-writeoff/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceItemsDetail
   * @param {string} invoiceId -
   */
  public async getInvoiceItemsDetail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceItemDetails;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceItemDetails> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceItemDetails>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceItemsDetailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceItemDetails>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceItemDetails
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceItemDetails,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceItemDetails>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceItemsDetailURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/details/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceDocument
   * @param {string} invoiceId -
   */
  public async getInvoiceDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoiceDocumentIdInResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoiceDocumentIdInResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoiceDocumentIdInResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoiceDocumentIdInResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoiceDocumentIdInResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoiceDocumentIdInResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoiceDocumentIdInResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceDocumentURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/invoice-document/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isDeleteWriteOffPossible
   * @param {string} invoiceId -
   */
  public async isDeleteWriteOffPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsWriteOffDeletionPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, IsWriteOffDeletionPossible> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, IsWriteOffDeletionPossible>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.isDeleteWriteOffPossibleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsWriteOffDeletionPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsWriteOffDeletionPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsWriteOffDeletionPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, IsWriteOffDeletionPossible>,
      header: result.header,
      hasError: true,
    };
  }

  public isDeleteWriteOffPossibleURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/is-delete-writeoff-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isRevokePaymentPossible
   * @param {string} bookingGroupId -
   * @param {string} invoiceId -
   */
  public async isRevokePaymentPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      bookingGroupId: string;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsPaymentSettlementRevocationPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, IsPaymentSettlementRevocationPossible>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, IsPaymentSettlementRevocationPossible>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['bookingGroupId'] === 'undefined') {
        reject(new Error('Missing required  parameter: bookingGroupId'));
        return;
      }

      if (typeof parameters['invoiceId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.isRevokePaymentPossibleURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsPaymentSettlementRevocationPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsPaymentSettlementRevocationPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsPaymentSettlementRevocationPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        IsPaymentSettlementRevocationPossible
      >,
      header: result.header,
      hasError: true,
    };
  }

  public isRevokePaymentPossibleURL(
    parameters: {
      bookingGroupId: string;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/is-revoke-payment-possible/{bookingGroupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{bookingGroupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['bookingGroupId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createPdfEmail
   * @param {boolean} createIfMissing -
   * @param {string} invoiceId -
   */
  public async createPdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['createIfMissing'] === 'undefined') {
          reject(new Error('Missing required  parameter: createIfMissing'));
          return;
        }

        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createPdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createPdfEmailURL(
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['createIfMissing'] !== 'undefined') {
      queryParameters['createIfMissing'] =
        Backend.convertParameterCollectionFormat(
          parameters['createIfMissing'],
          ''
        );
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createReminderPdfEmail
   * @param {boolean} createIfMissing -
   * @param {string} invoiceId -
   * @param {string} reminderId -
   */
  public async createReminderPdfEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
      reminderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['createIfMissing'] === 'undefined') {
          reject(new Error('Missing required  parameter: createIfMissing'));
          return;
        }

        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['reminderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: reminderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createReminderPdfEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createReminderPdfEmailURL(
    parameters: {
      createIfMissing: boolean;
      invoiceId: string;
      reminderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/reminder-pdf-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['createIfMissing'] !== 'undefined') {
      queryParameters['createIfMissing'] =
        Backend.convertParameterCollectionFormat(
          parameters['createIfMissing'],
          ''
        );
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['reminderId'] !== 'undefined') {
      queryParameters['reminderId'] = Backend.convertParameterCollectionFormat(
        parameters['reminderId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createReminderWordDocumentEmail
   * @param {string} invoiceId -
   * @param {string} reminderId -
   */
  public async createReminderWordDocumentEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
      reminderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        if (typeof parameters['reminderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: reminderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createReminderWordDocumentEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createReminderWordDocumentEmailURL(
    parameters: {
      invoiceId: string;
      reminderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/reminder-word-document-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['reminderId'] !== 'undefined') {
      queryParameters['reminderId'] = Backend.convertParameterCollectionFormat(
        parameters['reminderId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceReminders
   * @param {string} invoiceId -
   */
  public async getInvoiceReminders({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetInvoiceRemindersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetInvoiceRemindersResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetInvoiceRemindersResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoiceRemindersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetInvoiceRemindersResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetInvoiceRemindersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetInvoiceRemindersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetInvoiceRemindersResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceRemindersURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/reminders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createInvoiceReminder
   * @param {} body -
   * @param {string} invoiceId -
   */
  public async createInvoiceReminder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: InvoiceReminderCreateParams;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceReminderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceReminderResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreateInvoiceReminderResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['invoiceId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createInvoiceReminderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceReminderResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceReminderResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceReminderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreateInvoiceReminderResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createInvoiceReminderURL(
    parameters: {
      body: InvoiceReminderCreateParams;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/reminders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#revokePayment
   * @param {} body -
   * @param {string} bookingGroupId -
   * @param {string} invoiceId -
   */
  public async revokePayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: PaymentRevocationParams;
      bookingGroupId: string;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              PaymentSettlementRevocationNotPossibleCondition
            >
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentSettlementRevocationNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['bookingGroupId'] === 'undefined') {
        reject(new Error('Missing required  parameter: bookingGroupId'));
        return;
      }

      if (typeof parameters['invoiceId'] === 'undefined') {
        reject(new Error('Missing required  parameter: invoiceId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.revokePaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentSettlementRevocationNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              PaymentSettlementRevocationNotPossibleCondition
            >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<
            400,
            PaymentSettlementRevocationNotPossibleCondition
          >,
      header: result.header,
      hasError: true,
    };
  }

  public revokePaymentURL(
    parameters: {
      body: PaymentRevocationParams;
      bookingGroupId: string;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/invoices/{invoiceId}/revoke-payment/{bookingGroupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{bookingGroupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['bookingGroupId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#roundUnderpayment
   * @param {} body -
   * @param {string} invoiceId -
   */
  public async roundUnderpayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: RoundUnderpaymentParams;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.roundUnderpaymentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public roundUnderpaymentURL(
    parameters: {
      body: RoundUnderpaymentParams;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/round-underpayment/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createWordDocumentEmail
   * @param {string} invoiceId -
   */
  public async createWordDocumentEmail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateInvoiceEmailResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateInvoiceEmailResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateInvoiceEmailResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createWordDocumentEmailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateInvoiceEmailResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateInvoiceEmailResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateInvoiceEmailResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateInvoiceEmailResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createWordDocumentEmailURL(
    parameters: {
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/word-document-email/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#writeOffInvoice
   * @param {} body -
   * @param {string} invoiceId -
   */
  public async writeOffInvoice({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: WriteOffInvoiceParams;
      invoiceId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['invoiceId'] === 'undefined') {
          reject(new Error('Missing required  parameter: invoiceId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.writeOffInvoiceURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public writeOffInvoiceURL(
    parameters: {
      body: WriteOffInvoiceParams;
      invoiceId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/invoices/{invoiceId}/writeoff/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{invoiceId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['invoiceId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoicingMasterData
   */
  public async getInvoicingMasterData({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: InvoicingMasterData;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, InvoicingMasterData> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, InvoicingMasterData>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getInvoicingMasterDataURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, InvoicingMasterData>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          InvoicingMasterData
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as InvoicingMasterData,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, InvoicingMasterData>,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoicingMasterDataURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/masterdata/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPaymentBookings
   * @param {string} paymentId -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   */
  public async getPaymentBookings({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
      sortFields: Array<'CREATED_AT' | 'VALUE_DATE'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetBookingsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetBookingsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetBookingsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paymentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentId'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getPaymentBookingsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetBookingsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetBookingsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetBookingsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetBookingsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getPaymentBookingsURL(
    parameters: {
      paymentId: string;
      sortFields: Array<'CREATED_AT' | 'VALUE_DATE'>;
      sortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payment-bookings/{paymentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPayments
   * @param {array} bankAccountIds -
   * @param {array} casusIds -
   * @param {string} createdAtFromInclusive -
   * @param {string} createdAtToInclusive -
   * @param {array} createdByUserIds -
   * @param {array} debtorContactIds -
   * @param {string} hasReferenceNumber -
   * @param {array} officeLocationIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} state -
   * @param {string} valueDateFromInclusive -
   * @param {string} valueDateToInclusive -
   */
  public async getPayments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      bankAccountIds?: Array<string>;
      casusIds?: Array<string>;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      debtorContactIds?: Array<string>;
      hasReferenceNumber?: 'HAS_REFERENCE_NUMBER' | 'HAS_NO_REFERENCE_NUMBER';
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'PAYMENT_UNIQUE_NUMBER'
        | 'VALUE_DATE'
        | 'DEBTOR_DISPLAY_NAME'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'TOTAL_AMOUNT'
        | 'REMAINING_AMOUNT'
        | 'CREATED_AT'
        | 'VAT_NUMBER'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      state?: 'OPEN' | 'CONSUMED';
      valueDateFromInclusive?: string;
      valueDateToInclusive?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetPaymentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetPaymentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetPaymentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getPaymentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetPaymentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetPaymentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetPaymentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetPaymentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getPaymentsURL(
    parameters: {
      bankAccountIds?: Array<string>;
      casusIds?: Array<string>;
      createdAtFromInclusive?: string;
      createdAtToInclusive?: string;
      createdByUserIds?: Array<string>;
      debtorContactIds?: Array<string>;
      hasReferenceNumber?: 'HAS_REFERENCE_NUMBER' | 'HAS_NO_REFERENCE_NUMBER';
      officeLocationIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'PAYMENT_UNIQUE_NUMBER'
        | 'VALUE_DATE'
        | 'DEBTOR_DISPLAY_NAME'
        | 'CASUS_NUMBER'
        | 'CASUS_DISPLAY_NAME'
        | 'TOTAL_AMOUNT'
        | 'REMAINING_AMOUNT'
        | 'CREATED_AT'
        | 'VAT_NUMBER'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      state?: 'OPEN' | 'CONSUMED';
      valueDateFromInclusive?: string;
      valueDateToInclusive?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['bankAccountIds'] !== 'undefined') {
      queryParameters['bankAccountIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['bankAccountIds'],
          ''
        );
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (typeof parameters['createdAtFromInclusive'] !== 'undefined') {
      queryParameters['createdAtFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdAtToInclusive'] !== 'undefined') {
      queryParameters['createdAtToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdAtToInclusive'],
          ''
        );
    }

    if (typeof parameters['createdByUserIds'] !== 'undefined') {
      queryParameters['createdByUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdByUserIds'],
          ''
        );
    }

    if (typeof parameters['debtorContactIds'] !== 'undefined') {
      queryParameters['debtorContactIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['debtorContactIds'],
          ''
        );
    }

    if (typeof parameters['hasReferenceNumber'] !== 'undefined') {
      queryParameters['hasReferenceNumber'] =
        Backend.convertParameterCollectionFormat(
          parameters['hasReferenceNumber'],
          ''
        );
    }

    if (typeof parameters['officeLocationIds'] !== 'undefined') {
      queryParameters['officeLocationIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['officeLocationIds'],
          ''
        );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['state'] !== 'undefined') {
      queryParameters['state'] = Backend.convertParameterCollectionFormat(
        parameters['state'],
        ''
      );
    }

    if (typeof parameters['valueDateFromInclusive'] !== 'undefined') {
      queryParameters['valueDateFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['valueDateFromInclusive'],
          ''
        );
    }

    if (typeof parameters['valueDateToInclusive'] !== 'undefined') {
      queryParameters['valueDateToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['valueDateToInclusive'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createPayment
   * @param {} body -
   */
  public async createPayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreatePaymentParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreatePaymentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreatePaymentResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreatePaymentResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createPaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreatePaymentResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreatePaymentResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreatePaymentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreatePaymentResponse>
        | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createPaymentURL(
    parameters: {
      body: CreatePaymentParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deletePayment
   * @param {string} paymentId -
   */
  public async deletePayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, PaymentDeletionNotPossibleCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentDeletionNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deletePaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentDeletionNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, PaymentDeletionNotPossibleCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, PaymentDeletionNotPossibleCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deletePaymentURL(
    parameters: {
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getPaymentDetail
   * @param {string} paymentId -
   */
  public async getPaymentDetail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Payment;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Payment> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Payment>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['paymentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getPaymentDetailURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Payment>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Payment
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Payment,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Payment>,
      header: result.header,
      hasError: true,
    };
  }

  public getPaymentDetailURL(
    parameters: {
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getCasusIdForPayment
   * @param {string} paymentId -
   */
  public async getCasusIdForPayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getCasusIdForPayment_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getCasusIdForPayment_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getCasusIdForPayment_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getCasusIdForPaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getCasusIdForPayment_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getCasusIdForPayment_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getCasusIdForPayment_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getCasusIdForPayment_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getCasusIdForPaymentURL(
    parameters: {
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/casus-id/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isDeletePaymentPossible
   * @param {string} paymentId -
   */
  public async isDeletePaymentPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsPaymentDeletionPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, IsPaymentDeletionPossible> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, IsPaymentDeletionPossible>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paymentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.isDeletePaymentPossibleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsPaymentDeletionPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsPaymentDeletionPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsPaymentDeletionPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, IsPaymentDeletionPossible>,
      header: result.header,
      hasError: true,
    };
  }

  public isDeletePaymentPossibleURL(
    parameters: {
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/payments/{paymentId}/is-delete-payment-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isRoundOverpaymentPossible
   * @param {string} paymentId -
   */
  public async isRoundOverpaymentPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsRoundOverpaymentPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, IsRoundOverpaymentPossible> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, IsRoundOverpaymentPossible>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paymentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: paymentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.isRoundOverpaymentPossibleURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsRoundOverpaymentPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsRoundOverpaymentPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsRoundOverpaymentPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, IsRoundOverpaymentPossible>,
      header: result.header,
      hasError: true,
    };
  }

  public isRoundOverpaymentPossibleURL(
    parameters: {
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/payments/{paymentId}/is-round-overpayment-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deletePaymentBookingGroup
   * @param {string} paymentBookingGroupId -
   * @param {string} paymentId -
   */
  public async deletePaymentBookingGroup({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentBookingGroupId: string;
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              PaymentBookingGroupDeletionNotPossibleCondition
            >
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentBookingGroupDeletionNotPossibleCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paymentBookingGroupId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentBookingGroupId'));
        return;
      }

      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deletePaymentBookingGroupURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentBookingGroupDeletionNotPossibleCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<
              400,
              PaymentBookingGroupDeletionNotPossibleCondition
            >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<
            400,
            PaymentBookingGroupDeletionNotPossibleCondition
          >,
      header: result.header,
      hasError: true,
    };
  }

  public deletePaymentBookingGroupURL(
    parameters: {
      paymentBookingGroupId: string;
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/payments/{paymentId}/payment-booking-group/{paymentBookingGroupId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentBookingGroupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentBookingGroupId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isDeletePaymentBookingGroupPossible
   * @param {string} paymentBookingGroupId -
   * @param {string} paymentId -
   */
  public async isDeletePaymentBookingGroupPossible({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paymentBookingGroupId: string;
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsPaymentBookingGroupDeletionPossible;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, IsPaymentBookingGroupDeletionPossible>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, IsPaymentBookingGroupDeletionPossible>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paymentBookingGroupId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentBookingGroupId'));
        return;
      }

      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.isDeletePaymentBookingGroupPossibleURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsPaymentBookingGroupDeletionPossible>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsPaymentBookingGroupDeletionPossible
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsPaymentBookingGroupDeletionPossible,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        IsPaymentBookingGroupDeletionPossible
      >,
      header: result.header,
      hasError: true,
    };
  }

  public isDeletePaymentBookingGroupPossibleURL(
    parameters: {
      paymentBookingGroupId: string;
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/payments/{paymentId}/payment-booking-group/{paymentBookingGroupId}/is-delete-payment-possible/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentBookingGroupId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentBookingGroupId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#repayPayment
   * @param {} body -
   * @param {string} paymentId -
   */
  public async repayPayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: RepayPaymentParams;
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (ResponseWithBody<200, void> | ResponseWithBody<400, PaymentCondition>)
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, void> | ResponseWithBody<400, PaymentCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.repayPaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, PaymentCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, PaymentCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public repayPaymentURL(
    parameters: {
      body: RepayPaymentParams;
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/repay/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#roundOverpayment
   * @param {} body -
   * @param {string} paymentId -
   */
  public async roundOverpayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: RoundOverpaymentParams;
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (ResponseWithBody<200, void> | ResponseWithBody<400, PaymentCondition>)
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, void> | ResponseWithBody<400, PaymentCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.roundOverpaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, PaymentCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, PaymentCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, PaymentCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public roundOverpaymentURL(
    parameters: {
      body: RoundOverpaymentParams;
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/round-overpayment/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#transferPayment
   * @param {} body -
   * @param {string} paymentId -
   */
  public async transferPayment({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateTransferParams;
      paymentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreatePaymentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreatePaymentResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreatePaymentResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['paymentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: paymentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.transferPaymentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreatePaymentResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreatePaymentResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreatePaymentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreatePaymentResponse>
        | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public transferPaymentURL(
    parameters: {
      body: CreateTransferParams;
      paymentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/payments/{paymentId}/transfer/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{paymentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['paymentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#exportReceiptAsPdf
   * @param {} body -
   */
  public async exportReceiptAsPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: PdfReceiptParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.exportReceiptAsPdfURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public exportReceiptAsPdfURL(
    parameters: {
      body: PdfReceiptParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/receipts/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInvoiceReminderDefaults
   * @param {array} casusIds -
   */
  public async getInvoiceReminderDefaults({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetInvoiceReminderDefaultsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetInvoiceReminderDefaultsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetInvoiceReminderDefaultsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusIds'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusIds'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getInvoiceReminderDefaultsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetInvoiceReminderDefaultsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetInvoiceReminderDefaultsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetInvoiceReminderDefaultsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetInvoiceReminderDefaultsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getInvoiceReminderDefaultsURL(
    parameters: {
      casusIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/reminder-defaults/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusIds'] !== 'undefined') {
      queryParameters['casusIds'] = Backend.convertParameterCollectionFormat(
        parameters['casusIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getTemplates
   * @param {string} templateType -
   */
  public async getTemplates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      templateType:
        | 'DOCUMENT'
        | 'EMAIL'
        | 'INVOICE'
        | 'REMINDER'
        | 'JASPER_REPORT'
        | 'ADVANCE_PAYMENT_REQUEST'
        | 'ASPOSE_EXCEL';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetTemplatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetTemplatesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetTemplatesResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['templateType'] === 'undefined') {
          reject(new Error('Missing required  parameter: templateType'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getTemplatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetTemplatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetTemplatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetTemplatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetTemplatesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getTemplatesURL(
    parameters: {
      templateType:
        | 'DOCUMENT'
        | 'EMAIL'
        | 'INVOICE'
        | 'REMINDER'
        | 'JASPER_REPORT'
        | 'ADVANCE_PAYMENT_REQUEST'
        | 'ASPOSE_EXCEL';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['templateType'] !== 'undefined') {
      queryParameters['templateType'] =
        Backend.convertParameterCollectionFormat(
          parameters['templateType'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getOverridableTemplatePlaceholders
   * @param {string} casusId -
   * @param {string} invoiceId -
   * @param {string} invoiceReminderId -
   * @param {string} locale -
   * @param {string} templateFileId -
   * @param {string} templateId -
   */
  public async getOverridableTemplatePlaceholders({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      invoiceId?: string;
      invoiceReminderId?: string;
      locale: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetOverridableTemplatePlaceholdersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetOverridableTemplatePlaceholdersResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetOverridableTemplatePlaceholdersResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['locale'] === 'undefined') {
        reject(new Error('Missing required  parameter: locale'));
        return;
      }

      if (typeof parameters['templateFileId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateFileId'));
        return;
      }

      if (typeof parameters['templateId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getOverridableTemplatePlaceholdersURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      GetOverridableTemplatePlaceholdersResponse
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetOverridableTemplatePlaceholdersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetOverridableTemplatePlaceholdersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetOverridableTemplatePlaceholdersResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getOverridableTemplatePlaceholdersURL(
    parameters: {
      casusId: string;
      invoiceId?: string;
      invoiceReminderId?: string;
      locale: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/templates/{templateId}/template-files/{templateFileId}/placeholders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['invoiceId'] !== 'undefined') {
      queryParameters['invoiceId'] = Backend.convertParameterCollectionFormat(
        parameters['invoiceId'],
        ''
      );
    }

    if (typeof parameters['invoiceReminderId'] !== 'undefined') {
      queryParameters['invoiceReminderId'] =
        Backend.convertParameterCollectionFormat(
          parameters['invoiceReminderId'],
          ''
        );
    }

    if (typeof parameters['locale'] !== 'undefined') {
      queryParameters['locale'] = Backend.convertParameterCollectionFormat(
        parameters['locale'],
        ''
      );
    }

    path = path.replace(
      '{templateFileId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateFileId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{templateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getReportByDay
   * @param {string} clientId -
   * @param {string} date -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} userId -
   */
  public async getReportByDay({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientId?: string;
      date: string;
      sortFields: Array<'CASUS' | 'TOTAL_BEFORE_TAX'>;
      sortOrders: Array<'ASC' | 'DESC'>;
      userId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: DayBasedReport;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, DayBasedReport> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, DayBasedReport>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['date'] === 'undefined') {
          reject(new Error('Missing required  parameter: date'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        if (typeof parameters['userId'] === 'undefined') {
          reject(new Error('Missing required  parameter: userId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getReportByDayURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, DayBasedReport>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          DayBasedReport
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as DayBasedReport,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, DayBasedReport>,
      header: result.header,
      hasError: true,
    };
  }

  public getReportByDayURL(
    parameters: {
      clientId?: string;
      date: string;
      sortFields: Array<'CASUS' | 'TOTAL_BEFORE_TAX'>;
      sortOrders: Array<'ASC' | 'DESC'>;
      userId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/time-money-report/day-based/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['date'] !== 'undefined') {
      queryParameters['date'] = Backend.convertParameterCollectionFormat(
        parameters['date'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['userId'] !== 'undefined') {
      queryParameters['userId'] = Backend.convertParameterCollectionFormat(
        parameters['userId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteFee
   * @param {string} feeId -
   */
  public async deleteFee({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      feeId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (ResponseWithBody<200, void> | ResponseWithBody<400, FeeInUseCondition>)
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, void> | ResponseWithBody<400, FeeInUseCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['feeId'] === 'undefined') {
        reject(new Error('Missing required  parameter: feeId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteFeeURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, FeeInUseCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, FeeInUseCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, FeeInUseCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteFeeURL(
    parameters: {
      feeId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/time-money-report/day-based/fees/{feeId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{feeId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['feeId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getReportByPeriod
   * @param {string} clientId -
   * @param {string} date -
   * @param {string} periodType -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {string} userId -
   */
  public async getReportByPeriod({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      clientId?: string;
      date: string;
      periodType: 'WEEK' | 'MONTH';
      sortFields: Array<'CASUS'>;
      sortOrders: Array<'ASC' | 'DESC'>;
      userId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: PeriodReport;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, PeriodReport> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, PeriodReport>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['date'] === 'undefined') {
          reject(new Error('Missing required  parameter: date'));
          return;
        }

        if (typeof parameters['periodType'] === 'undefined') {
          reject(new Error('Missing required  parameter: periodType'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        if (typeof parameters['userId'] === 'undefined') {
          reject(new Error('Missing required  parameter: userId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getReportByPeriodURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, PeriodReport>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          PeriodReport
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as PeriodReport,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, PeriodReport>,
      header: result.header,
      hasError: true,
    };
  }

  public getReportByPeriodURL(
    parameters: {
      clientId?: string;
      date: string;
      periodType: 'WEEK' | 'MONTH';
      sortFields: Array<'CASUS'>;
      sortOrders: Array<'ASC' | 'DESC'>;
      userId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/time-money-report/period-based/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['clientId'] !== 'undefined') {
      queryParameters['clientId'] = Backend.convertParameterCollectionFormat(
        parameters['clientId'],
        ''
      );
    }

    if (typeof parameters['date'] !== 'undefined') {
      queryParameters['date'] = Backend.convertParameterCollectionFormat(
        parameters['date'],
        ''
      );
    }

    if (typeof parameters['periodType'] !== 'undefined') {
      queryParameters['periodType'] = Backend.convertParameterCollectionFormat(
        parameters['periodType'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['userId'] !== 'undefined') {
      queryParameters['userId'] = Backend.convertParameterCollectionFormat(
        parameters['userId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getTransactionAssignmentCandidates
   * @param {string} casusId -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {string} type -
   */
  public async getTransactionAssignmentCandidates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      type?: 'INVOICE' | 'DEBTOR';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionAssignmentCandidatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, TransactionAssignmentCandidatesResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionAssignmentCandidatesResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getTransactionAssignmentCandidatesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionAssignmentCandidatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionAssignmentCandidatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionAssignmentCandidatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionAssignmentCandidatesResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getTransactionAssignmentCandidatesURL(
    parameters: {
      casusId?: string;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      type?: 'INVOICE' | 'DEBTOR';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/transaction-assignment-candidates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['type'] !== 'undefined') {
      queryParameters['type'] = Backend.convertParameterCollectionFormat(
        parameters['type'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getTransactionSets
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} states -
   */
  public async getTransactionSets({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      sortFields: Array<
        'STATUS' | 'PERIOD_FROM' | 'PERIOD_TO' | 'TOTAL' | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<'UPLOADED' | 'IN_PROGRESS' | 'DONE'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, TransactionSetsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getTransactionSetsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, TransactionSetsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getTransactionSetsURL(
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      sortFields: Array<
        'STATUS' | 'PERIOD_FROM' | 'PERIOD_TO' | 'TOTAL' | 'CREATED_AT'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      states?: Array<'UPLOADED' | 'IN_PROGRESS' | 'DONE'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/transaction-sets/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['states'] !== 'undefined') {
      queryParameters['states'] = Backend.convertParameterCollectionFormat(
        parameters['states'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#importTransactionSet
   * @param {file} file -
   * @param {string} bankAccountId -
   */
  public async importTransactionSet({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      bankAccountId?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: ImportTransactionSetResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, ImportTransactionSetResponse>
          | ResponseWithBody<400, ImportTransactionSetEndpointCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, ImportTransactionSetResponse>
      | ResponseWithBody<400, ImportTransactionSetEndpointCondition>
    > = new Promise((resolve, reject) => {
      headers['content-type'] = 'multipart/form-data';

      if (typeof parameters['file'] !== 'undefined') {
        form['file'] = parameters['file'];
      }

      if (typeof parameters['file'] === 'undefined') {
        reject(new Error('Missing required  parameter: file'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.importTransactionSetURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, ImportTransactionSetResponse>
      | ResponseWithBody<400, ImportTransactionSetEndpointCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, ImportTransactionSetResponse>
          | ResponseWithBody<400, ImportTransactionSetEndpointCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as ImportTransactionSetResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, ImportTransactionSetResponse>
        | ResponseWithBody<400, ImportTransactionSetEndpointCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public importTransactionSetURL(
    parameters: {
      file: {};
      bankAccountId?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/transaction-sets/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['bankAccountId'] !== 'undefined') {
      queryParameters['bankAccountId'] =
        Backend.convertParameterCollectionFormat(
          parameters['bankAccountId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteTransactionSet
   * @param {string} transactionSetId -
   */
  public async deleteTransactionSet({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      transactionSetId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['transactionSetId'] === 'undefined') {
          reject(new Error('Missing required  parameter: transactionSetId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteTransactionSetURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteTransactionSetURL(
    parameters: {
      transactionSetId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/transaction-sets/{transactionSetId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#assignMatchedTransactions
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async assignMatchedTransactions({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.assignMatchedTransactionsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public assignMatchedTransactionsURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/assign-matched-transactions/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#exportTransactionSetAsPdf
   * @param {string} transactionSetId -
   */
  public async exportTransactionSetAsPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      transactionSetId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['transactionSetId'] === 'undefined') {
          reject(new Error('Missing required  parameter: transactionSetId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.exportTransactionSetAsPdfURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public exportTransactionSetAsPdfURL(
    parameters: {
      transactionSetId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/invoice-management/transaction-sets/{transactionSetId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getTransactionSetTransactions
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async getTransactionSetTransactions({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getTransactionSetTransactionsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getTransactionSetTransactionsURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#assignMatchedTransaction
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionId -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async assignMatchedTransaction({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionId'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.assignMatchedTransactionURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public assignMatchedTransactionURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/{transactionId}/assign-matched-transaction/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#assignTransaction
   * @param {} body -
   * @param {string} transactionId -
   * @param {string} transactionSetId -
   */
  public async assignTransaction({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: AssignTransactionParams;
      transactionId: string;
      transactionSetId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: AssignTransactionResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, AssignTransactionResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, AssignTransactionResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['transactionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionId'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.assignTransactionURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, AssignTransactionResponse>
      | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, AssignTransactionResponse>
          | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as AssignTransactionResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, AssignTransactionResponse>
        | ResponseWithBody<400, UnexpectedInvoiceRemainingAmountCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public assignTransactionURL(
    parameters: {
      body: AssignTransactionParams;
      transactionId: string;
      transactionSetId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/{transactionId}/assign-transaction/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{transactionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markTransactionAsDuplicate
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionId -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async markTransactionAsDuplicate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionId'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.markTransactionAsDuplicateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public markTransactionAsDuplicateURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/{transactionId}/mark-transaction-as-duplicate/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#markTransactionAsIgnored
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionId -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async markTransactionAsIgnored({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionId'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.markTransactionAsIgnoredURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public markTransactionAsIgnoredURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/{transactionId}/mark-transaction-as-ignored/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#resetTransactionState
   * @param {array} assignedSortFields -
   * @param {array} assignedSortOrders -
   * @param {array} duplicateSortFields -
   * @param {array} duplicateSortOrders -
   * @param {array} ignoredSortFields -
   * @param {array} ignoredSortOrders -
   * @param {array} matchedSortFields -
   * @param {array} matchedSortOrders -
   * @param {string} transactionId -
   * @param {string} transactionSetId -
   * @param {array} unmatchedSortFields -
   * @param {array} unmatchedSortOrders -
   */
  public async resetTransactionState({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TransactionSetTransactionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TransactionSetTransactionsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, TransactionSetTransactionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['assignedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortFields'));
        return;
      }

      if (typeof parameters['assignedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: assignedSortOrders'));
        return;
      }

      if (typeof parameters['duplicateSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortFields'));
        return;
      }

      if (typeof parameters['duplicateSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: duplicateSortOrders'));
        return;
      }

      if (typeof parameters['ignoredSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortFields'));
        return;
      }

      if (typeof parameters['ignoredSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: ignoredSortOrders'));
        return;
      }

      if (typeof parameters['matchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortFields'));
        return;
      }

      if (typeof parameters['matchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: matchedSortOrders'));
        return;
      }

      if (typeof parameters['transactionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionId'));
        return;
      }

      if (typeof parameters['transactionSetId'] === 'undefined') {
        reject(new Error('Missing required  parameter: transactionSetId'));
        return;
      }

      if (typeof parameters['unmatchedSortFields'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortFields'));
        return;
      }

      if (typeof parameters['unmatchedSortOrders'] === 'undefined') {
        reject(new Error('Missing required  parameter: unmatchedSortOrders'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.resetTransactionStateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TransactionSetTransactionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TransactionSetTransactionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TransactionSetTransactionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        TransactionSetTransactionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public resetTransactionStateURL(
    parameters: {
      assignedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      assignedSortOrders: Array<'ASC' | 'DESC'>;
      duplicateSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      duplicateSortOrders: Array<'ASC' | 'DESC'>;
      ignoredSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      ignoredSortOrders: Array<'ASC' | 'DESC'>;
      matchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      matchedSortOrders: Array<'ASC' | 'DESC'>;
      transactionId: string;
      transactionSetId: string;
      unmatchedSortFields: Array<
        | 'VALUE_DATE'
        | 'INVOICE_UNIQUE_NUMBER'
        | 'CASUS'
        | 'REFERENCE_NUMBER'
        | 'PAYER'
        | 'INVOICE_RECIPIENT'
        | 'AMOUNT'
      >;
      unmatchedSortOrders: Array<'ASC' | 'DESC'>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/invoice-management/transaction-sets/{transactionSetId}/transactions/{transactionId}/reset-transaction-state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['assignedSortFields'] !== 'undefined') {
      queryParameters['assignedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortFields'],
          ''
        );
    }

    if (typeof parameters['assignedSortOrders'] !== 'undefined') {
      queryParameters['assignedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['assignedSortOrders'],
          ''
        );
    }

    if (typeof parameters['duplicateSortFields'] !== 'undefined') {
      queryParameters['duplicateSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortFields'],
          ''
        );
    }

    if (typeof parameters['duplicateSortOrders'] !== 'undefined') {
      queryParameters['duplicateSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['duplicateSortOrders'],
          ''
        );
    }

    if (typeof parameters['ignoredSortFields'] !== 'undefined') {
      queryParameters['ignoredSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortFields'],
          ''
        );
    }

    if (typeof parameters['ignoredSortOrders'] !== 'undefined') {
      queryParameters['ignoredSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['ignoredSortOrders'],
          ''
        );
    }

    if (typeof parameters['matchedSortFields'] !== 'undefined') {
      queryParameters['matchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortFields'],
          ''
        );
    }

    if (typeof parameters['matchedSortOrders'] !== 'undefined') {
      queryParameters['matchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['matchedSortOrders'],
          ''
        );
    }

    path = path.replace(
      '{transactionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{transactionSetId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['transactionSetId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['unmatchedSortFields'] !== 'undefined') {
      queryParameters['unmatchedSortFields'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortFields'],
          ''
        );
    }

    if (typeof parameters['unmatchedSortOrders'] !== 'undefined') {
      queryParameters['unmatchedSortOrders'] =
        Backend.convertParameterCollectionFormat(
          parameters['unmatchedSortOrders'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }
}

const backend = new Backend(
  process.env.REACT_APP_SERVER_URL || '/api',
  (error) => {
    if (error.status === 401) {
      window.location.href =
        '/login/?redirect=' +
        encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
    }
  }
);

backend.setConfigureRequestHandler(
  (request: SuperAgentRequest): SuperAgentRequest => {
    if (new RegExp('/download', 'u').test(request.url)) {
      request.responseType('blob');
    }

    return request;
  }
);

export default backend;
